'use client'

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ComponentProps, type MouseEvent, type ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Tabs,
  Tab,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  TextField,
  InputAdornment,
  Fade,
  useMediaQuery,
  useTheme,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Menu,
  MenuItem,
  Collapse,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Badge,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded'
import PublicRoundedIcon from '@mui/icons-material/PublicRounded'
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import ThumbDownOffAltOutlinedIcon from '@mui/icons-material/ThumbDownOffAltOutlined'
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import PollOutlinedIcon from '@mui/icons-material/PollOutlined'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import { outfit } from '@/lib/fonts'
import { fileToResizedDataUrl } from '@/lib/networkProfileImage'
import { isChatPanelSuperAdmin } from '@/lib/opasSessionRole'
import { readChatNotificationPrefs, writeChatNotificationPrefs, loadChatNotificationPrefs, getDefaultChatNotificationPrefs } from '@/lib/chatNotificationPrefs'
import {
  resolveNetworkAvatarDisplaySrc,
  resolveNetworkCoverDisplaySrc,
  markNetworkAvatarMissing,
  markNetworkAvatarPresent,
  markNetworkUserAvatarMissing,
  markNetworkUserAvatarPresent,
  parseMediaVersion,
  prefetchNetworkAvatars,
  resolveNetworkUserAvatarDisplaySrc,
} from '@/lib/networkProfileMediaUrl'
import { usePharmacyNetworkUserProfile } from '@/hooks/usePharmacyNetworkUserProfile'
import { OpasBrandLoading, OpasBrandSpinner } from '@/components/branding/OpasBrandLoading'
import { usePharmacyNetworkProfileMedia, fetchNetworkProfileMedia } from '@/hooks/usePharmacyNetworkProfileMedia'
import { usePharmacyInternalMessages, type InternalMessageDto } from '@/hooks/usePharmacyInternalMessages'
import {
  usePharmacyNetworkFeed,
  type PharmacyPostDto,
  type PharmacyPostCommentDto,
  type PharmacyPostImageDto,
  type PharmacyPostPollDto,
} from '@/hooks/usePharmacyNetworkFeed'
import {
  usePharmacyNetworkDm,
  usePharmacyNetworkFollows,
  usePharmacyNetworkSettings,
  usePharmacyNetworkThreads,
  type NetworkDmMessageDto,
  type NetworkDmSearchHit,
  type NetworkDmThreadDto,
  type NetworkFollowRequestIncomingDto,
} from '@/hooks/usePharmacyNetworkDm'
import {
  usePharmacyNetworkBlocks,
  usePharmacyNetworkBans,
  usePharmacyNetworkModeration,
  usePharmacyNetworkWarnings,
  type NetworkModerationReportDto,
  type NetworkModerationTicketDto,
} from '@/hooks/usePharmacyNetworkModeration'
import { usePharmacyNetworkSupport, type SupportTicketDto } from '@/hooks/usePharmacyNetworkSupport'
import type { ChatOpenTarget } from '@/hooks/useChatNotifications'
import { emitAppToast } from '@/lib/appToast'
import { useHataBildirici, useOpasDialog } from '@/components/ui/OpasDialogSaglayici'
import type { Theme } from '@mui/material/styles'
import type { SxProps } from '@mui/material/styles'

/** Mesajlar drawer `modal+10` — içindeki menü/dialog bunun üstünde açılmalı */
const CHAT_DRAWER_Z_OFFSET = 10
const chatPanelOverlayZ = (t: Pick<Theme, 'zIndex'>) => t.zIndex.modal + CHAT_DRAWER_Z_OFFSET + 10

/** Yumuşak pastel palet — panel içi */
const pastel = {
  bg: 'linear-gradient(165deg, #f8f5ff 0%, #fff8f5 38%, #f3fcf8 100%)',
  border: 'rgba(196, 181, 253, 0.45)',
  lavender: '#e8e0ff',
  mint: '#d1fae5',
  peach: '#ffedd5',
  text: '#4c4458',
  textMuted: '#7a7189',
  shadow: '0 24px 48px rgba(91, 71, 120, 0.12)',
}

const chatPanelModalPrimaryBtnSx = {
  textTransform: 'none' as const,
  fontWeight: 700,
  fontFamily: outfit.style.fontFamily,
  borderRadius: 2,
  px: 2.25,
  minWidth: 92,
  background: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
  color: '#fff',
  boxShadow: '0 6px 18px rgba(124, 58, 237, 0.28)',
  '&:hover': { background: 'linear-gradient(135deg, #9333ea 0%, #6d28d9 100%)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.75)', background: 'linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)' },
}

const chatPanelModalGhostBtnSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  fontFamily: outfit.style.fontFamily,
  color: pastel.textMuted,
  borderRadius: 2,
  px: 1.5,
  '&:hover': { bgcolor: 'rgba(196,181,253,0.18)', color: pastel.text },
}

/** Drawer içi modal — portal yok; OPAS mesaj paneli stili. */
function ChatPanelModal({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  maxWidth = 400,
  fullBleed,
  backdropSx,
  paperSx,
}: {
  open: boolean
  onClose?: () => void
  title?: string
  subtitle?: string
  children?: ReactNode
  actions?: ReactNode
  maxWidth?: number | string
  fullBleed?: boolean
  backdropSx?: SxProps<Theme>
  paperSx?: SxProps<Theme>
}) {
  if (!open) return null

  return (
    <Box
      role="dialog"
      aria-modal
      aria-labelledby={title ? 'chat-panel-modal-title' : undefined}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 40,
        display: 'flex',
        alignItems: fullBleed ? 'stretch' : 'center',
        justifyContent: 'center',
        px: fullBleed ? 0 : 1.5,
        py: fullBleed ? 0 : 2,
      }}
    >
      <Box
        aria-hidden
        onClick={() => onClose?.()}
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'rgba(35, 28, 48, 0.42)',
          backdropFilter: 'blur(6px)',
          ...backdropSx,
        }}
      />
      <Paper
        elevation={0}
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: fullBleed ? '100%' : maxWidth,
          maxHeight: fullBleed ? '100%' : 'calc(100% - 16px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: fullBleed ? 0 : 3,
          bgcolor: fullBleed ? 'transparent' : '#fff',
          border: fullBleed ? 'none' : `1px solid ${pastel.border}`,
          boxShadow: fullBleed ? 'none' : '0 20px 50px rgba(91, 71, 120, 0.22)',
          fontFamily: outfit.style.fontFamily,
          ...(paperSx as object),
        }}
      >
        {!fullBleed && (title || onClose) && (
          <Box
            sx={{
              px: 2.25,
              pt: 2,
              pb: subtitle ? 1.25 : 1.75,
              background: 'linear-gradient(180deg, rgba(248,245,255,0.95) 0%, rgba(255,255,255,1) 100%)',
              borderBottom: `1px solid rgba(196,181,253,0.28)`,
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                {title && (
                  <Typography
                    id="chat-panel-modal-title"
                    className={outfit.className}
                    sx={{ fontWeight: 800, fontSize: '1rem', color: pastel.text, lineHeight: 1.25 }}
                  >
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography className={outfit.className} sx={{ fontSize: '0.78rem', color: pastel.textMuted, mt: 0.65, lineHeight: 1.45 }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
              {onClose && (
                <IconButton
                  size="small"
                  aria-label="Kapat"
                  onClick={onClose}
                  sx={{ color: pastel.textMuted, mt: -0.25, '&:hover': { bgcolor: 'rgba(196,181,253,0.22)', color: pastel.text } }}
                >
                  <CloseRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Stack>
          </Box>
        )}

        {!fullBleed && children && (
          <Box sx={{ px: 2.25, py: 2, overflow: 'auto', color: pastel.text, flex: 1 }}>{children}</Box>
        )}

        {fullBleed && children}

        {!fullBleed && actions && (
          <Box
            sx={{
              px: 2.25,
              py: 1.75,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
              borderTop: `1px solid rgba(196,181,253,0.22)`,
              bgcolor: 'rgba(255,255,255,0.96)',
            }}
          >
            {actions}
          </Box>
        )}
      </Paper>
    </Box>
  )
}

/** Açık arka planlı alanlarda koyu modda da okunaklı metin */
const networkFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: 'rgba(255,255,255,0.95)',
    fontFamily: outfit.style.fontFamily,
    fontSize: '0.875rem',
  },
  '& .MuiOutlinedInput-input': {
    color: pastel.text,
  },
  '& .MuiOutlinedInput-input::placeholder': {
    opacity: 1,
    color: pastel.textMuted,
  },
} as const

/** Duvar paylaşım kutusu — belirgin, modern kart */
const networkComposeBoxSx = {
  mx: 2,
  mb: 1,
  p: 1.35,
  borderRadius: 3,
  bgcolor: 'rgba(255,255,255,0.94)',
  border: '1px solid rgba(196,181,253,0.38)',
  boxShadow: '0 8px 28px rgba(91,71,120,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
  backgroundImage: 'linear-gradient(165deg, rgba(255,255,255,0.98) 0%, rgba(248,245,255,0.92) 100%)',
  '& .MuiInputBase-root': {
    fontFamily: outfit.style.fontFamily,
    fontSize: '0.92rem',
    lineHeight: 1.5,
    color: pastel.text,
  },
  '& .MuiInputBase-input::placeholder': {
    opacity: 1,
    color: pastel.textMuted,
  },
} as const

const outfitFont = outfit.style.fontFamily

/** Duvar compose ikonları — ince, modern ipucu */
const networkIconTooltipProps = {
  placement: 'top' as const,
  enterDelay: 420,
  enterNextDelay: 120,
  slotProps: {
    tooltip: {
      sx: {
        fontFamily: outfitFont,
        fontSize: '0.7rem',
        fontWeight: 500,
        lineHeight: 1.35,
        px: 1,
        py: 0.35,
        borderRadius: 1.25,
        color: pastel.text,
        bgcolor: 'rgba(255,255,255,0.97)',
        border: '1px solid rgba(196,181,253,0.32)',
        boxShadow: '0 6px 18px rgba(91,71,120,0.1)',
        backdropFilter: 'blur(10px)',
        letterSpacing: '0.01em',
      },
    },
    popper: {
      sx: { zIndex: (t: Theme) => chatPanelOverlayZ(t) + 2 },
    },
  },
}

/** Paylaşım/yorum ⋯ menüsü — minimal floating kart */
const networkActionMenuPaperSx = {
  mt: 0.5,
  minWidth: 152,
  borderRadius: 2.5,
  bgcolor: 'rgba(255,255,255,0.98)',
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(196,181,253,0.18)',
  boxShadow: '0 12px 40px rgba(91,71,120,0.1)',
  overflow: 'hidden',
  py: 0.35,
  px: 0.35,
  '& .MuiList-root': { py: 0 },
} as const

const networkActionMenuItemSx = {
  fontFamily: `${outfitFont} !important`,
  fontSize: '0.75rem',
  fontWeight: 500,
  color: pastel.text,
  borderRadius: 2,
  py: 0.75,
  px: 1.1,
  gap: 0.85,
  minHeight: 34,
  transition: 'background-color 0.15s ease, color 0.15s ease',
  '&:hover': {
    bgcolor: 'rgba(237,233,254,0.85)',
    color: '#5b21b6',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 15,
    color: 'rgba(122,113,137,0.75)',
    transition: 'color 0.15s ease',
  },
  '&:hover .MuiSvgIcon-root': {
    color: '#7c3aed',
  },
} as const

const networkProfileBtnSx = {
  textTransform: 'none' as const,
  fontFamily: `${outfitFont} !important`,
  fontWeight: 700,
  borderRadius: 2,
  fontSize: '0.82rem',
  minHeight: 36,
  px: 2,
  boxShadow: 'none',
}

/** Profil paneli — tema/MUI font ezmesini engelle (Outfit yalnızca 400/700) */
const networkProfileOutfitScope = {
  fontFamily: `${outfitFont} !important`,
  '& .MuiTypography-root': { fontFamily: `${outfitFont} !important` },
  '& .MuiButton-root': { fontFamily: `${outfitFont} !important`, fontWeight: 700 },
  '& .MuiAvatar-root': { fontFamily: `${outfitFont} !important`, fontWeight: 700 },
} as const

function NetworkProfileText({
  children,
  sx,
  bold,
  ...props
}: ComponentProps<typeof Typography> & { bold?: boolean }) {
  return (
    <Typography
      {...props}
      className={outfit.className}
      sx={{
        fontFamily: `${outfitFont} !important`,
        fontWeight: bold ? 700 : 400,
        ...sx,
      }}
    >
      {children}
    </Typography>
  )
}

export interface TenantChatPanelProps {
  open: boolean
  onClose: () => void
  openTarget?: ChatOpenTarget | null
  onOpenTargetConsumed?: () => void
}

type ChannelTab = 'internal' | 'network' | 'support'
type SupportQueueTab = 'pending' | 'resolved'

const supportTabsSx = {
  minHeight: 36,
  borderBottom: `1px solid ${pastel.border}`,
  px: 1,
  '& .MuiTab-root': {
    minHeight: 36,
    py: 0.5,
    textTransform: 'none',
    fontFamily: outfit.style.fontFamily,
    fontSize: '0.78rem',
    fontWeight: 600,
  },
} as const

const NETWORK_AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #6366f1, #8b5cf6)',
  'linear-gradient(135deg, #0ea5e9, #06b6d4)',
  'linear-gradient(135deg, #10b981, #34d399)',
  'linear-gradient(135deg, #f59e0b, #f97316)',
  'linear-gradient(135deg, #ec4899, #f43f5e)',
]

function networkGradient(seed: string) {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return NETWORK_AVATAR_GRADIENTS[(h >>> 0) % NETWORK_AVATAR_GRADIENTS.length]
}

function nameInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function formatRelativeTime(iso: string) {
  try {
    const d = new Date(iso)
    const mins = Math.floor((Date.now() - d.getTime()) / 60000)
    if (mins < 1) return 'Az önce'
    if (mins < 60) return `${mins} dk`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours} sa`
    return formatTime(iso)
  } catch {
    return ''
  }
}

function formatTime(iso: string) {
  try {
    const d = new Date(iso)
    return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

/** Kullanıcı kimliğine göre sabit, yumuşak pastel balon teması */
const USER_BUBBLE_THEMES = [
  { bg: '#f3e8ff', border: 'rgba(167, 139, 250, 0.5)', name: '#6b21a8', body: '#3d2f50' },
  { bg: '#e0f2fe', border: 'rgba(56, 189, 248, 0.45)', name: '#0369a1', body: '#1e3a4a' },
  { bg: '#d1fae5', border: 'rgba(52, 211, 153, 0.45)', name: '#047857', body: '#1a3d32' },
  { bg: '#ffedd5', border: 'rgba(251, 146, 60, 0.45)', name: '#c2410c', body: '#422c20' },
  { bg: '#fce7f3', border: 'rgba(244, 114, 182, 0.45)', name: '#9d174d', body: '#4a1d35' },
  { bg: '#fef3c7', border: 'rgba(251, 191, 36, 0.5)', name: '#b45309', body: '#3f2e12' },
  { bg: '#e9d5ff', border: 'rgba(192, 132, 252, 0.45)', name: '#7e22ce', body: '#342047' },
  { bg: '#cffafe', border: 'rgba(34, 211, 238, 0.45)', name: '#0e7490', body: '#164e63' },
  { bg: '#fecdd3', border: 'rgba(251, 113, 133, 0.45)', name: '#be123c', body: '#451a24' },
  { bg: '#ddd6fe', border: 'rgba(139, 92, 246, 0.45)', name: '#5b21b6', body: '#2e1065' },
] as const

function bubbleThemeForSender(senderUserId: string) {
  let h = 2166136261
  for (let i = 0; i < senderUserId.length; i++) {
    h ^= senderUserId.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return USER_BUBBLE_THEMES[(h >>> 0) % USER_BUBBLE_THEMES.length]
}

function InternalChannel({
  panelOpen,
  tabActive,
}: {
  panelOpen: boolean
  tabActive: boolean
}) {
  const enabled = panelOpen && tabActive
  const { messages, loading, connected, error, send } = usePharmacyInternalMessages(enabled)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const bottomRef = useRef<HTMLDivElement | null>(null)
  const [myUserId, setMyUserId] = useState<string | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    setMyUserId(localStorage.getItem('userId'))
  }, [])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  const handleSend = async () => {
    const t = draft.trim()
    if (!t || sending) return
    setSending(true)
    setDraft('')
    try {
      await send(t)
    } catch {
      setDraft(t)
    } finally {
      setSending(false)
    }
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, p: 2, pt: 1 }}>
      {error && !loading && (
        <Typography className={outfit.className} sx={{ fontSize: '0.75rem', color: '#b91c1c', mb: 1 }}>
          {error}
        </Typography>
      )}
      <Box
        sx={{
          flex: 1,
          borderRadius: 2,
          p: 1.5,
          mb: 1.5,
          background: 'rgba(255,255,255,0.75)',
          border: `1px solid rgba(196, 181, 253, 0.2)`,
          overflow: 'auto',
          minHeight: 120,
        }}
      >
        {loading && messages.length === 0 ? (
          <OpasBrandLoading label="Mesajlar yükleniyor…" sx={{ minHeight: 100, py: 2 }} />
        ) : (
          <>
            {messages.length === 0 && !loading && (
              <Typography className={outfit.className} sx={{ color: pastel.textMuted, fontSize: '0.85rem' }}>
                Henüz mesaj yok. Aşağıdan yazarak başlayın.
              </Typography>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} m={m} own={myUserId != null && m.senderUserId === myUserId} />
            ))}
          </>
        )}
        <div ref={bottomRef} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Mesaj yazın…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void handleSend()
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => void handleSend()}
                  disabled={!draft.trim() || sending}
                  aria-label="Gönder"
                >
                  {sending ? <OpasBrandSpinner size={18} /> : <SendRoundedIcon sx={{ color: pastel.textMuted, fontSize: 20 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.95)',
              fontFamily: outfit.style.fontFamily,
              fontSize: '0.875rem',
            },
            '& .MuiOutlinedInput-input': {
              color: pastel.text,
            },
            '& .MuiOutlinedInput-input::placeholder': {
              opacity: 1,
              color: `${pastel.textMuted}`,
            },
          }}
        />
      </Box>
      <Typography className={outfit.className} sx={{ fontSize: '0.65rem', color: pastel.textMuted, mt: 0.75 }}>
        {connected ? '● Canlı' : loading ? '● Bağlanıyor' : 'Bağlantı yok'}
      </Typography>
    </Box>
  )
}

function MessageBubble({ m, own }: { m: InternalMessageDto; own: boolean }) {
  const displayName = m.senderUsername?.trim() || m.senderUserId.slice(0, 10)
  const theme = bubbleThemeForSender(m.senderUserId)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: own ? 'flex-end' : 'flex-start',
        mb: 1.35,
        maxWidth: '94%',
        alignSelf: own ? 'flex-end' : 'flex-start',
        ml: own ? 'auto' : 0,
        mr: own ? 0 : 'auto',
      }}
    >
      <Box sx={{ px: 0.5, mb: 0.5, width: '100%', textAlign: own ? 'right' : 'left' }}>
        <Typography
          className={outfit.className}
          component="span"
          sx={{
            display: 'block',
            fontWeight: 800,
            fontSize: '0.82rem',
            letterSpacing: '-0.02em',
            color: theme.name,
            lineHeight: 1.25,
            textShadow: '0 1px 0 rgba(255,255,255,0.6)',
          }}
        >
          {displayName}
        </Typography>
        <Typography
          className={outfit.className}
          component="span"
          sx={{
            display: 'block',
            fontSize: '0.62rem',
            color: pastel.textMuted,
            fontWeight: 500,
            mt: 0.15,
          }}
        >
          {formatTime(m.createdAtUtc)}
        </Typography>
      </Box>
      <Box
        sx={{
          px: 1.35,
          py: 1.1,
          borderRadius: 2.25,
          bgcolor: theme.bg,
          border: `1.5px solid ${theme.border}`,
          boxShadow: own ? '0 2px 12px rgba(91, 71, 120, 0.08)' : '0 1px 8px rgba(91, 71, 120, 0.06)',
          color: theme.body,
          fontSize: '0.875rem',
          fontFamily: outfit.style.fontFamily,
          fontWeight: 700,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: 1.45,
        }}
      >
        {m.body}
      </Box>
    </Box>
  )
}

type NetworkSubTab = 'wall' | 'messages'

type NetworkPharmacyProfile = {
  tenantId: string
  username: string
  pharmacyName: string
  gln: string
  city?: string | null
  town?: string | null
  userType: string
  isOwnPharmacy: boolean
  avatarUrl?: string | null
  coverUrl?: string | null
  profileBio?: string | null
}

function NetworkProfileAvatar({
  username,
  tenantId,
  userId,
  avatarUrl,
  size,
  sx,
  onClick,
  variant = 'user',
}: {
  username: string
  tenantId: string
  userId?: string
  avatarUrl?: string | null
  size: number
  sx?: ComponentProps<typeof Avatar>['sx']
  onClick?: () => void
  /** user = paylaşım/yorum; pharmacy = eczane logosu */
  variant?: 'user' | 'pharmacy'
}) {
  const src = useMemo(() => {
    if (variant === 'pharmacy' || !userId) {
      return resolveNetworkAvatarDisplaySrc(tenantId, avatarUrl)
    }
    return resolveNetworkUserAvatarDisplaySrc(tenantId, userId, avatarUrl)
  }, [tenantId, userId, avatarUrl, variant])
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [src])

  const showInitials = !src || failed

  return (
    <Avatar
      src={failed ? undefined : src}
      onClick={onClick}
      imgProps={{
        loading: 'eager',
        decoding: 'async',
        onError: () => {
          if (src && !src.startsWith('data:')) {
            if (variant === 'pharmacy' || !userId) markNetworkAvatarMissing(tenantId)
            else markNetworkUserAvatarMissing(tenantId, userId)
          }
          setFailed(true)
        },
        onLoad: () => {
          if (src && !src.startsWith('data:')) {
            const version = parseMediaVersion(src)
            if (variant === 'pharmacy' || !userId) markNetworkAvatarPresent(tenantId, version)
            else markNetworkUserAvatarPresent(tenantId, userId, version)
          }
          setFailed(false)
        },
      }}
      sx={{
        width: size,
        height: size,
        background: showInitials ? networkGradient(tenantId) : '#f8f5ff',
        fontSize: size < 40 ? '0.62rem' : '1.35rem',
        fontFamily: outfit.style.fontFamily,
        fontWeight: 700,
        cursor: onClick ? 'pointer' : undefined,
        ...sx,
      }}
    >
      {showInitials ? nameInitials(username) : null}
    </Avatar>
  )
}

function profileFromPost(post: PharmacyPostDto): NetworkPharmacyProfile {
  const pharmacyName = post.pharmacyDisplayName?.trim() || `GLN ${post.authorGln}`
  return {
    tenantId: post.tenantId,
    username: post.authorUsername?.trim() || pharmacyName,
    pharmacyName,
    gln: post.authorGln,
    city: post.pharmacyCity,
    town: post.pharmacyTown,
    userType: post.authorUserType,
    isOwnPharmacy: post.isOwnPharmacy,
  }
}

function profileFromComment(comment: PharmacyPostCommentDto, tenantRef?: PharmacyPostDto): NetworkPharmacyProfile {
  const pharmacyName = comment.pharmacyDisplayName?.trim() || tenantRef?.pharmacyDisplayName?.trim() || 'Eczane'
  return {
    tenantId: comment.tenantId,
    username: comment.authorUsername?.trim() || pharmacyName,
    pharmacyName,
    gln: tenantRef?.authorGln ?? '',
    city: comment.pharmacyCity ?? tenantRef?.pharmacyCity ?? null,
    town: comment.pharmacyTown ?? tenantRef?.pharmacyTown ?? null,
    userType: comment.authorUserType,
    isOwnPharmacy: tenantRef?.isOwnPharmacy ?? false,
  }
}

function NetworkPharmacyProfilePanel({
  profile,
  onBack,
  isFollowing,
  isPendingOutgoing,
  followBusy,
  onFollow,
  onUnfollow,
  onCancelOutgoing,
  recentPosts,
  onMediaUpdated,
}: {
  profile: NetworkPharmacyProfile
  onBack: () => void
  isFollowing: boolean
  isPendingOutgoing: boolean
  followBusy: boolean
  onFollow: () => void
  onUnfollow: () => void
  onCancelOutgoing: () => void
  recentPosts: PharmacyPostDto[]
  onMediaUpdated?: (tenantId: string, avatarUrl: string | null, coverUrl: string | null) => void
}) {
  const hataBildir = useHataBildirici()
  const { media, loading: mediaLoading, saving: mediaSaving, updateMedia } = usePharmacyNetworkProfileMedia(
    profile.tenantId,
    true,
    { avatarUrl: profile.avatarUrl, coverUrl: profile.coverUrl, profileBio: profile.profileBio }
  )
  const {
    profile: userProfile,
    saving: userAvatarSaving,
    updateAvatar: updateUserAvatar,
  } = usePharmacyNetworkUserProfile(profile.isOwnPharmacy)
  const avatarInputRef = useRef<HTMLInputElement | null>(null)
  const userAvatarInputRef = useRef<HTMLInputElement | null>(null)
  const coverInputRef = useRef<HTMLInputElement | null>(null)
  const [previewAvatar, setPreviewAvatar] = useState<string | null>(null)
  const [previewUserAvatar, setPreviewUserAvatar] = useState<string | null>(null)
  const [previewCover, setPreviewCover] = useState<string | null>(null)
  const [bioDraft, setBioDraft] = useState(profile.profileBio ?? '')
  const [bioSaving, setBioSaving] = useState(false)

  const canEditPharmacyLogo =
    profile.isOwnPharmacy &&
    (profile.userType === 'tenant' ||
      (typeof window !== 'undefined' && localStorage.getItem('userType') === 'tenant'))

  const avatarUrl = previewAvatar ?? media?.avatarUrl ?? profile.avatarUrl ?? null
  const userAvatarUrl = previewUserAvatar ?? userProfile?.avatarUrl ?? null
  const coverUrl = previewCover ?? media?.coverUrl ?? profile.coverUrl ?? null
  const coverDisplaySrc = useMemo(
    () => resolveNetworkCoverDisplaySrc(profile.tenantId, coverUrl),
    [profile.tenantId, coverUrl]
  )
  const profileBio = media?.profileBio ?? profile.profileBio ?? null

  useEffect(() => {
    setBioDraft(profileBio ?? '')
  }, [profileBio])
  const mediaSyncedKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!media || !onMediaUpdated) return
    const syncKey = `${profile.tenantId}|${media.avatarUrl ?? ''}|${media.coverUrl ?? ''}`
    if (mediaSyncedKeyRef.current === syncKey) return
    mediaSyncedKeyRef.current = syncKey
    onMediaUpdated(profile.tenantId, media.avatarUrl ?? null, media.coverUrl ?? null)
  }, [media, onMediaUpdated, profile.tenantId])

  const handleAvatarPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !canEditPharmacyLogo) return
    try {
      const dataUrl = await fileToResizedDataUrl(file, 512)
      setPreviewAvatar(dataUrl)
      const next = await updateMedia({ avatarDataUrl: dataUrl })
      setPreviewAvatar(null)
      onMediaUpdated?.(profile.tenantId, next.avatarUrl ?? null, next.coverUrl ?? null)
    } catch (err) {
      setPreviewAvatar(null)
      hataBildir(err, 'Eczane logosu yüklenemedi')
    }
  }

  const handleUserAvatarPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !profile.isOwnPharmacy) return
    try {
      const dataUrl = await fileToResizedDataUrl(file, 512)
      setPreviewUserAvatar(dataUrl)
      await updateUserAvatar({ avatarDataUrl: dataUrl })
      setPreviewUserAvatar(null)
    } catch (err) {
      setPreviewUserAvatar(null)
      hataBildir(err, 'Profil fotoğrafı yüklenemedi')
    }
  }

  const handleCoverPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !profile.isOwnPharmacy) return
    try {
      const dataUrl = await fileToResizedDataUrl(file, 1280)
      setPreviewCover(dataUrl)
      const next = await updateMedia({ coverDataUrl: dataUrl })
      setPreviewCover(null)
      onMediaUpdated?.(profile.tenantId, next.avatarUrl ?? null, next.coverUrl ?? null)
    } catch (err) {
      setPreviewCover(null)
      hataBildir(err, 'Kapak fotoğrafı yüklenemedi')
    }
  }

  const handleClearAvatar = async () => {
    if (!canEditPharmacyLogo) return
    try {
      const next = await updateMedia({ clearAvatar: true })
      onMediaUpdated?.(profile.tenantId, next.avatarUrl ?? null, next.coverUrl ?? null)
    } catch (err) {
      hataBildir(err, 'Eczane logosu kaldırılamadı')
    }
  }

  const handleClearUserAvatar = async () => {
    if (!profile.isOwnPharmacy) return
    try {
      await updateUserAvatar({ clearAvatar: true })
    } catch (err) {
      hataBildir(err, 'Profil fotoğrafı kaldırılamadı')
    }
  }

  const handleClearCover = async () => {
    if (!profile.isOwnPharmacy) return
    try {
      const next = await updateMedia({ clearCover: true })
      onMediaUpdated?.(profile.tenantId, next.avatarUrl ?? null, next.coverUrl ?? null)
    } catch (err) {
      hataBildir(err, 'Kapak fotoğrafı kaldırılamadı')
    }
  }

  const handleSaveBio = async () => {
    if (!profile.isOwnPharmacy) return
    setBioSaving(true)
    try {
      await updateMedia({ profileBio: bioDraft.trim() || null })
    } catch (err) {
      hataBildir(err, 'Profil açıklaması kaydedilemedi')
    } finally {
      setBioSaving(false)
    }
  }

  const location = [profile.town, profile.city].filter(Boolean).join(', ')
  const roleLabel = profile.userType === 'tenant' ? 'Eczacı' : 'Ekip üyesi'
  const postCount = recentPosts.length

  const infoRows = [
    { label: 'Eczane', value: profile.pharmacyName },
    { label: 'GLN', value: profile.gln },
    ...(location ? [{ label: 'Konum', value: location }] : []),
    { label: 'Rol', value: roleLabel },
  ]

  return (
    <Box
      className={outfit.className}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        ...networkProfileOutfitScope,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.75,
          flexShrink: 0,
          borderBottom: `1px solid ${pastel.border}`,
          bgcolor: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <IconButton size="small" onClick={onBack} aria-label="Duvara dön" sx={{ color: pastel.text }}>
          <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <NetworkProfileText
          bold
          sx={{
            flex: 1,
            fontSize: '0.92rem',
            color: pastel.text,
            textAlign: 'center',
            pr: 4.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {profile.username}
        </NetworkProfileText>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', ...networkProfileOutfitScope }}>
        <Box
          sx={{
            position: 'relative',
            height: 120,
            background: coverDisplaySrc ? undefined : networkGradient(profile.tenantId),
            backgroundImage: coverDisplaySrc ? `url(${coverDisplaySrc})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: coverUrl
                ? 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(248,245,255,0.88) 100%)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(248,245,255,0.92) 100%)',
            }}
          />
          {profile.isOwnPharmacy && (
            <>
              <input ref={coverInputRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => void handleCoverPick(e)} />
              <Stack direction="row" spacing={0.75} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                <IconButton
                  size="small"
                  aria-label="Kapak fotoğrafı düzenle"
                  disabled={mediaSaving}
                  onClick={() => coverInputRef.current?.click()}
                  sx={{ bgcolor: 'rgba(255,255,255,0.88)', border: `1px solid ${pastel.border}`, '&:hover': { bgcolor: '#fff' } }}
                >
                  {mediaSaving ? <OpasBrandSpinner size={16} /> : <PhotoCameraOutlinedIcon sx={{ fontSize: 17, color: pastel.text }} />}
                </IconButton>
                {coverUrl && (
                  <IconButton
                    size="small"
                    aria-label="Kapak fotoğrafını kaldır"
                    disabled={mediaSaving}
                    onClick={() => void handleClearCover()}
                    sx={{ bgcolor: 'rgba(255,255,255,0.88)', border: `1px solid ${pastel.border}`, '&:hover': { bgcolor: '#fff' } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 17, color: pastel.textMuted }} />
                  </IconButton>
                )}
              </Stack>
            </>
          )}
        </Box>

        <Box sx={{ px: 2, pb: 2.5, mt: -4.5 }}>
          <Box sx={{ mb: 1.25, position: 'relative', width: 88 }}>
            {canEditPharmacyLogo && (
              <input ref={avatarInputRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => void handleAvatarPick(e)} />
            )}
            <NetworkProfileAvatar
              username={profile.pharmacyName}
              tenantId={profile.tenantId}
              avatarUrl={avatarUrl}
              variant="pharmacy"
              size={80}
              sx={{
                border: '4px solid #fff',
                boxShadow: '0 8px 24px rgba(91, 71, 120, 0.18)',
                fontFamily: `${outfitFont} !important`,
              }}
            />
            {mediaLoading && (
              <Box sx={{ position: 'absolute', inset: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.55)' }}>
                <OpasBrandSpinner size={22} />
              </Box>
            )}
            {canEditPharmacyLogo && (
              <Stack direction="row" spacing={0.35} sx={{ position: 'absolute', right: -2, bottom: 2 }}>
                <IconButton
                  size="small"
                  aria-label="Eczane logosu düzenle"
                  disabled={mediaSaving}
                  onClick={() => avatarInputRef.current?.click()}
                  sx={{ bgcolor: '#fff', border: `1px solid ${pastel.border}`, boxShadow: '0 2px 8px rgba(91,71,120,0.12)', '&:hover': { bgcolor: pastel.lavender } }}
                >
                  <PhotoCameraOutlinedIcon sx={{ fontSize: 15, color: pastel.text }} />
                </IconButton>
                {avatarUrl && (
                  <IconButton
                    size="small"
                    aria-label="Eczane logosunu kaldır"
                    disabled={mediaSaving}
                    onClick={() => void handleClearAvatar()}
                    sx={{ bgcolor: '#fff', border: `1px solid ${pastel.border}`, boxShadow: '0 2px 8px rgba(91,71,120,0.12)', '&:hover': { bgcolor: pastel.lavender } }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 15, color: pastel.textMuted }} />
                  </IconButton>
                )}
              </Stack>
            )}
          </Box>

          {profile.isOwnPharmacy && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, mb: 1.1, p: 1, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.72)', border: `1px solid ${pastel.border}` }}>
              <input ref={userAvatarInputRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => void handleUserAvatarPick(e)} />
              <NetworkProfileAvatar
                username={profile.username}
                tenantId={profile.tenantId}
                userId={userProfile?.userId ?? (typeof window !== 'undefined' ? localStorage.getItem('userId') ?? undefined : undefined)}
                avatarUrl={userAvatarUrl}
                size={44}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <NetworkProfileText sx={{ fontSize: '0.72rem', fontWeight: 700, color: pastel.text }}>Profil fotoğrafım</NetworkProfileText>
                <NetworkProfileText sx={{ fontSize: '0.62rem', color: pastel.textMuted, lineHeight: 1.35 }}>
                  Paylaşım ve yorumlarda görünür; sadece size aittir.
                </NetworkProfileText>
              </Box>
              <Stack direction="row" spacing={0.35}>
                <IconButton
                  size="small"
                  aria-label="Profil fotoğrafımı düzenle"
                  disabled={userAvatarSaving}
                  onClick={() => userAvatarInputRef.current?.click()}
                  sx={{ bgcolor: '#fff', border: `1px solid ${pastel.border}` }}
                >
                  {userAvatarSaving ? <OpasBrandSpinner size={14} /> : <PhotoCameraOutlinedIcon sx={{ fontSize: 15, color: pastel.text }} />}
                </IconButton>
                {userAvatarUrl && (
                  <IconButton
                    size="small"
                    aria-label="Profil fotoğrafımı kaldır"
                    disabled={userAvatarSaving}
                    onClick={() => void handleClearUserAvatar()}
                    sx={{ bgcolor: '#fff', border: `1px solid ${pastel.border}` }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 15, color: pastel.textMuted }} />
                  </IconButton>
                )}
              </Stack>
            </Box>
          )}

          <NetworkProfileText bold sx={{ fontSize: '1.22rem', color: pastel.text, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            {profile.username}
          </NetworkProfileText>

          <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.5 }}>
            <StorefrontRoundedIcon sx={{ fontSize: 15, color: pastel.textMuted }} />
            <NetworkProfileText sx={{ fontSize: '0.88rem', color: pastel.textMuted }}>
              {profile.pharmacyName}
            </NetworkProfileText>
          </Stack>

          {profile.isOwnPharmacy ? (
            <Box sx={{ mt: 1.25 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                size="small"
                placeholder="Eczaneniz hakkında kısa bir açıklama…"
                value={bioDraft}
                onChange={(e) => setBioDraft(e.target.value.slice(0, 500))}
                sx={networkFieldSx}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                <NetworkProfileText sx={{ fontSize: '0.62rem', color: pastel.textMuted }}>
                  {bioDraft.length}/500
                </NetworkProfileText>
                <Button
                  className={outfit.className}
                  size="small"
                  variant="outlined"
                  disabled={bioSaving || bioDraft.trim() === (profileBio ?? '')}
                  onClick={() => void handleSaveBio()}
                  sx={{ ...networkProfileBtnSx, minHeight: 28, color: `${pastel.text} !important`, borderColor: `${pastel.border} !important` }}
                >
                  {bioSaving ? <OpasBrandSpinner size={14} /> : 'Açıklamayı kaydet'}
                </Button>
              </Box>
            </Box>
          ) : profileBio ? (
            <NetworkProfileText sx={{ mt: 1.1, fontSize: '0.84rem', color: pastel.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {profileBio}
            </NetworkProfileText>
          ) : null}

          {!profile.isOwnPharmacy && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
              {isFollowing ? (
                <>
                  <Button
                    className={outfit.className}
                    variant="outlined"
                    size="small"
                    disabled
                    startIcon={<CheckRoundedIcon sx={{ fontSize: 16, color: pastel.text }} />}
                    sx={{
                      ...networkProfileBtnSx,
                      color: `${pastel.text} !important`,
                      borderColor: `${pastel.border} !important`,
                      bgcolor: '#fff',
                      '&.Mui-disabled': {
                        color: `${pastel.text} !important`,
                        borderColor: `${pastel.border} !important`,
                        opacity: 1,
                      },
                    }}
                  >
                    Takiptesin
                  </Button>
                  <Button
                    className={outfit.className}
                    variant="outlined"
                    size="small"
                    disabled={followBusy}
                    onClick={() => void onUnfollow()}
                    sx={{
                      ...networkProfileBtnSx,
                      color: `${pastel.text} !important`,
                      borderColor: `${pastel.border} !important`,
                      bgcolor: '#fff',
                      '&:hover': {
                        bgcolor: 'rgba(254, 226, 226, 0.45)',
                        borderColor: '#f87171',
                        color: '#b91c1c !important',
                      },
                    }}
                  >
                    Bırak
                  </Button>
                </>
              ) : isPendingOutgoing ? (
                <>
                  <Button
                    className={outfit.className}
                    variant="outlined"
                    size="small"
                    disabled
                    sx={{
                      ...networkProfileBtnSx,
                      color: `${pastel.textMuted} !important`,
                      borderColor: `${pastel.border} !important`,
                      bgcolor: '#fff',
                      '&.Mui-disabled': { opacity: 1, color: `${pastel.textMuted} !important` },
                    }}
                  >
                    Onay bekleniyor
                  </Button>
                  <Button
                    className={outfit.className}
                    variant="outlined"
                    size="small"
                    disabled={followBusy}
                    onClick={() => void onCancelOutgoing()}
                    sx={{
                      ...networkProfileBtnSx,
                      color: `${pastel.text} !important`,
                      borderColor: `${pastel.border} !important`,
                      bgcolor: '#fff',
                    }}
                  >
                    İptal
                  </Button>
                </>
              ) : (
                <Button
                  className={outfit.className}
                  variant="contained"
                  size="small"
                  disabled={followBusy}
                  startIcon={
                    followBusy ? (
                      <OpasBrandSpinner size={15} color="#fff" />
                    ) : (
                      <PersonAddRoundedIcon sx={{ fontSize: 17, color: '#fff' }} />
                    )
                  }
                  onClick={() => void onFollow()}
                  sx={{
                    ...networkProfileBtnSx,
                    color: '#fff !important',
                    bgcolor: '#5b21b6',
                    '&:hover': { bgcolor: '#4c1d95', boxShadow: 'none' },
                  }}
                >
                  Ekle
                </Button>
              )}
            </Stack>
          )}

          {location && (
            <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.45 }}>
              <LocationOnOutlinedIcon sx={{ fontSize: 15, color: pastel.textMuted }} />
              <NetworkProfileText sx={{ fontSize: '0.8rem', color: pastel.textMuted }}>
                {location}
              </NetworkProfileText>
            </Stack>
          )}

          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
            <VerifiedRoundedIcon sx={{ fontSize: 16, color: '#6366f1' }} />
            <NetworkProfileText bold sx={{ fontSize: '0.76rem', color: '#6366f1' }}>
              OPAS eczane ağı üyesi
            </NetworkProfileText>
          </Stack>

          {profile.isOwnPharmacy && (
            <Box
              sx={{
                mt: 1.25,
                py: 0.85,
                px: 1.25,
                borderRadius: 2,
                bgcolor: pastel.lavender,
                border: `1px solid ${pastel.border}`,
              }}
            >
              <NetworkProfileText bold sx={{ fontSize: '0.8rem', color: pastel.text, textAlign: 'center' }}>
                Bu sizin eczane profiliniz
              </NetworkProfileText>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              gap: 0,
              mt: 2,
              py: 1.25,
              borderTop: `1px solid ${pastel.border}`,
              borderBottom: `1px solid ${pastel.border}`,
            }}
          >
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <NetworkProfileText bold sx={{ fontSize: '1.05rem', color: pastel.text }}>
                {postCount}
              </NetworkProfileText>
              <NetworkProfileText sx={{ fontSize: '0.72rem', color: pastel.textMuted, mt: 0.15 }}>
                Paylaşım
              </NetworkProfileText>
            </Box>
            <Box sx={{ width: '1px', bgcolor: pastel.border, my: 0.25 }} />
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <NetworkProfileText bold sx={{ fontSize: '1.05rem', color: pastel.text }}>
                {isFollowing ? '✓' : isPendingOutgoing ? '…' : '—'}
              </NetworkProfileText>
              <NetworkProfileText sx={{ fontSize: '0.72rem', color: pastel.textMuted, mt: 0.15 }}>
                Bağlantı
              </NetworkProfileText>
            </Box>
          </Box>

          <NetworkProfileText
            bold
            sx={{
              fontSize: '0.72rem',
              color: pastel.textMuted,
              mt: 2,
              mb: 0.85,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Bilgiler
          </NetworkProfileText>

          <Box
            sx={{
              borderRadius: 2.5,
              overflow: 'hidden',
              bgcolor: 'rgba(255,255,255,0.92)',
              border: '1px solid rgba(196,181,253,0.32)',
              boxShadow: '0 2px 16px rgba(91, 71, 120, 0.06)',
            }}
          >
            {infoRows.map((row, i) => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                  px: 1.5,
                  py: 1.1,
                  borderBottom: i < infoRows.length - 1 ? `1px solid rgba(196,181,253,0.2)` : 'none',
                }}
              >
                <NetworkProfileText sx={{ fontSize: '0.78rem', color: pastel.textMuted, flexShrink: 0 }}>
                  {row.label}
                </NetworkProfileText>
                <NetworkProfileText bold sx={{ fontSize: '0.8rem', color: pastel.text, textAlign: 'right', wordBreak: 'break-word' }}>
                  {row.value}
                </NetworkProfileText>
              </Box>
            ))}
          </Box>

          {recentPosts.length > 0 && (
            <>
              <NetworkProfileText
                bold
                sx={{
                  fontSize: '0.72rem',
                  color: pastel.textMuted,
                  mt: 2,
                  mb: 0.85,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Son paylaşımlar
              </NetworkProfileText>
              <Stack spacing={0.85}>
                {recentPosts.map((p) => (
                  <Box
                    key={p.id}
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.88)',
                      border: '1px solid rgba(196,181,253,0.25)',
                    }}
                  >
                    <NetworkProfileText sx={{ fontSize: '0.68rem', color: pastel.textMuted, mb: 0.4 }}>
                      {formatRelativeTime(p.createdAtUtc)}
                    </NetworkProfileText>
                    <NetworkProfileText
                      sx={{
                        fontSize: '0.82rem',
                        color: pastel.text,
                        lineHeight: 1.45,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {p.body}
                    </NetworkProfileText>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

type CommentNode = PharmacyPostCommentDto & { children: CommentNode[] }

const NETWORK_REPORT_REASONS = [
  { value: 'spam', label: 'Spam / reklam' },
  { value: 'harassment', label: 'Taciz veya nefret söylemi' },
  { value: 'misinformation', label: 'Yanıltıcı bilgi' },
  { value: 'other', label: 'Diğer' },
] as const

function NetworkReportDialog({
  open,
  onClose,
  onSubmit,
  submitting,
}: {
  open: boolean
  onClose: () => void
  onSubmit: (reason: string, note: string) => Promise<void>
  submitting: boolean
}) {
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')

  useEffect(() => {
    if (!open) {
      setReason('')
      setNote('')
    }
  }, [open])

  return (
    <ChatPanelModal
      open={open}
      onClose={submitting ? undefined : onClose}
      title="Bildir / şikayet et"
      subtitle="Uygunsuz içeriği ekibimize bildirin. İnceleme sonrası gerekli işlem yapılır."
      actions={
        <>
          <Button onClick={onClose} disabled={submitting} sx={chatPanelModalGhostBtnSx}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            disabled={!reason || submitting}
            onClick={() => void onSubmit(reason, note.trim())}
            sx={chatPanelModalPrimaryBtnSx}
          >
            {submitting ? <OpasBrandSpinner size={16} color="#fff" /> : 'Gönder'}
          </Button>
        </>
      }
    >
      <FormControl
        fullWidth
        size="small"
        sx={{
          mb: 1.25,
          '& .MuiInputLabel-root': { color: pastel.textMuted, fontFamily: outfit.style.fontFamily },
          '& .MuiInputLabel-root.Mui-focused': { color: '#7c3aed' },
        }}
      >
        <InputLabel id="network-report-reason">Neden</InputLabel>
        <Select
          labelId="network-report-reason"
          label="Neden"
          value={reason}
          onChange={(e) => setReason(String(e.target.value))}
          sx={{
            fontFamily: outfit.style.fontFamily,
            fontSize: '0.82rem',
            color: pastel.text,
            bgcolor: 'rgba(255,255,255,0.95)',
            borderRadius: 2,
          }}
          MenuProps={{ disablePortal: true, sx: { zIndex: 50 } }}
        >
          {NETWORK_REPORT_REASONS.map((r) => (
            <MenuItem key={r.value} value={r.value} sx={{ fontFamily: outfit.style.fontFamily, fontSize: '0.82rem', color: pastel.text }}>
              {r.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        size="small"
        multiline
        minRows={3}
        maxRows={5}
        label="Ek açıklama (isteğe bağlı)"
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 500))}
        sx={networkFieldSx}
      />
    </ChatPanelModal>
  )
}

function NetworkPollBlock({
  poll,
  disabled,
  onVote,
}: {
  poll: PharmacyPostPollDto
  disabled?: boolean
  onVote: (optionId: string) => void
}) {
  const hasVoted = Boolean(poll.votedOptionId)
  const showResults = hasVoted || poll.isClosed
  const total = poll.totalVotes

  return (
    <Box sx={{ mt: 0.85, p: 1, borderRadius: 1.75, bgcolor: 'rgba(255,255,255,0.72)', border: '1px solid rgba(196,181,253,0.28)' }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
        <PollOutlinedIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
        <Typography className={outfit.className} sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#6d28d9', letterSpacing: '0.04em' }}>
          ANKET
        </Typography>
        <Typography className={outfit.className} sx={{ fontSize: '0.68rem', color: pastel.textMuted, ml: 'auto' }}>
          {total} oy{poll.isClosed ? ' · kapandı' : ''}
        </Typography>
      </Stack>
      <Stack spacing={0.65}>
        {poll.options.map((opt) => {
          const pct = total > 0 ? Math.round((opt.voteCount / total) * 100) : 0
          const isMine = poll.votedOptionId === opt.id
          return (
            <Box key={opt.id}>
              {showResults ? (
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                    <Typography className={outfit.className} sx={{ fontSize: '0.78rem', fontWeight: isMine ? 800 : 600, color: pastel.text }}>
                      {opt.label}
                      {isMine && (
                        <Typography component="span" sx={{ fontSize: '0.65rem', color: '#7c3aed', ml: 0.5 }}>
                          · sizin oyunuz
                        </Typography>
                      )}
                    </Typography>
                    <Typography className={outfit.className} sx={{ fontSize: '0.72rem', fontWeight: 700, color: pastel.textMuted }}>
                      {pct}% · {opt.voteCount}
                    </Typography>
                  </Stack>
                  <Box sx={{ height: 6, borderRadius: 999, bgcolor: 'rgba(196,181,253,0.25)', overflow: 'hidden' }}>
                    <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: isMine ? '#a78bfa' : '#c4b5fd', transition: 'width 0.35s ease' }} />
                  </Box>
                </Box>
              ) : (
                <Button
                  fullWidth
                  size="small"
                  disabled={disabled}
                  onClick={() => onVote(opt.id)}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontFamily: outfit.style.fontFamily,
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: pastel.text,
                    borderRadius: 1.25,
                    border: `1px solid rgba(196,181,253,0.45)`,
                    bgcolor: '#fff',
                    py: 0.65,
                    '&:hover': { bgcolor: 'rgba(243,232,255,0.65)', borderColor: '#a78bfa' },
                  }}
                >
                  {opt.label}
                </Button>
              )}
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

function buildCommentTree(comments: PharmacyPostCommentDto[]): CommentNode[] {
  const map = new Map<string, CommentNode>()
  const roots: CommentNode[] = []
  for (const c of comments) map.set(c.id, { ...c, children: [] })
  for (const c of comments) {
    const node = map.get(c.id)!
    if (c.parentId && map.has(c.parentId)) map.get(c.parentId)!.children.push(node)
    else roots.push(node)
  }
  const sortNodes = (nodes: CommentNode[]) => {
    nodes.sort((a, b) => new Date(a.createdAtUtc).getTime() - new Date(b.createdAtUtc).getTime())
    nodes.forEach((n) => sortNodes(n.children))
  }
  sortNodes(roots)
  return roots
}

const MAX_NETWORK_POST_IMAGES = 2

async function pickNetworkPostImages(
  files: FileList | null,
  current: string[],
  onDone: (next: string[]) => void,
  onHata: (hata: unknown, varsayilanMesaj: string) => void
) {
  if (!files?.length) return
  const remaining = MAX_NETWORK_POST_IMAGES - current.length
  if (remaining <= 0) return
  try {
    const urls = await Promise.all(
      Array.from(files)
        .slice(0, remaining)
        .map((f) => fileToResizedDataUrl(f, 2048))
    )
    onDone([...current, ...urls].slice(0, MAX_NETWORK_POST_IMAGES))
  } catch (e) {
    onHata(e, 'Görsel eklenemedi')
  }
}

function NetworkPostImageLightbox({
  images,
  index,
  open,
  onClose,
  onIndexChange,
}: {
  images: PharmacyPostImageDto[]
  index: number
  open: boolean
  onClose: () => void
  onIndexChange: (index: number) => void
}) {
  const current = images[index]
  const hasMultiple = images.length > 1

  const goPrev = () => onIndexChange((index - 1 + images.length) % images.length)
  const goNext = () => onIndexChange((index + 1) % images.length)

  return (
    <ChatPanelModal
      open={open}
      onClose={onClose}
      fullBleed
      backdropSx={{ bgcolor: 'rgba(15, 11, 30, 0.92)', backdropFilter: 'none' }}
      paperSx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'visible' }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          px: hasMultiple ? { xs: 4, sm: 6 } : 0,
        }}
      >
        <IconButton
          aria-label="Kapat"
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.14)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
          }}
        >
          <CloseRoundedIcon />
        </IconButton>

        {hasMultiple && (
          <IconButton
            aria-label="Önceki görsel"
            onClick={goPrev}
            sx={{
              position: 'absolute',
              left: { xs: 8, sm: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.14)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
            }}
          >
            <ChevronLeftRoundedIcon sx={{ fontSize: 28 }} />
          </IconButton>
        )}

        {current ? (
          <Box
            component="img"
            src={current.url}
            alt=""
            sx={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: 1.5,
              boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
            }}
          />
        ) : null}

        {hasMultiple && (
          <IconButton
            aria-label="Sonraki görsel"
            onClick={goNext}
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 16 },
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.14)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
            }}
          >
            <ChevronRightRoundedIcon sx={{ fontSize: 28 }} />
          </IconButton>
        )}

        {hasMultiple && (
          <Typography
            className={outfit.className}
            sx={{
              position: 'absolute',
              bottom: 14,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'rgba(255,255,255,0.82)',
              fontSize: '0.78rem',
              fontWeight: 600,
            }}
          >
            {index + 1} / {images.length}
          </Typography>
        )}
      </Box>
    </ChatPanelModal>
  )
}

function NetworkPostImages({ images, compact }: { images?: PharmacyPostImageDto[]; compact?: boolean }) {
  const [lightbox, setLightbox] = useState({ open: false, index: 0 })
  if (!images?.length) return null
  const single = images.length === 1
  const maxWidth = compact ? 200 : 480
  const maxHeight = compact ? 260 : 560
  const eager = !compact

  return (
    <>
      <Box
        sx={{
          mt: compact ? 0.45 : 0.75,
          display: 'flex',
          flexDirection: single ? 'column' : 'row',
          flexWrap: 'wrap',
          gap: 0.65,
          maxWidth: single ? maxWidth : maxWidth * 2 + 6,
        }}
      >
        {images.map((img, idx) => (
          <Box
            key={img.id}
            component="img"
            src={img.url}
            alt=""
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            fetchPriority={eager && idx === 0 ? 'high' : undefined}
            sx={{
              display: 'block',
              width: 'auto',
              height: 'auto',
              maxWidth: single ? '100%' : `calc(50% - 4px)`,
              maxHeight,
              objectFit: 'contain',
              borderRadius: 1.5,
              border: '1px solid rgba(196,181,253,0.28)',
              bgcolor: 'rgba(255,255,255,0.92)',
              cursor: 'zoom-in',
              transition: 'opacity 0.15s ease',
              '&:hover': { opacity: 0.92 },
            }}
            onClick={() => setLightbox({ open: true, index: idx })}
          />
        ))}
      </Box>
      <NetworkPostImageLightbox
        images={images}
        index={lightbox.index}
        open={lightbox.open}
        onClose={() => setLightbox((prev) => ({ ...prev, open: false }))}
        onIndexChange={(index) => setLightbox({ open: true, index })}
      />
    </>
  )
}

function NetworkComposeImagePreviews({
  images,
  onRemove,
}: {
  images: string[]
  onRemove: (index: number) => void
}) {
  if (images.length === 0) return null
  return (
    <Stack direction="row" spacing={0.65} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
      {images.map((src, idx) => (
        <Box key={`${idx}-${src.slice(0, 24)}`} sx={{ position: 'relative' }}>
          <Box
            component="img"
            src={src}
            alt=""
            sx={{
              width: 72,
              height: 72,
              objectFit: 'contain',
              borderRadius: 1.25,
              border: '1px solid rgba(196,181,253,0.35)',
              bgcolor: 'rgba(255,255,255,0.92)',
            }}
          />
          <IconButton
            size="small"
            aria-label="Görseli kaldır"
            onClick={() => onRemove(idx)}
            sx={{
              position: 'absolute',
              top: -6,
              right: -6,
              width: 20,
              height: 20,
              bgcolor: '#fff',
              border: '1px solid rgba(196,181,253,0.45)',
              '&:hover': { bgcolor: '#fef2f2' },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 13, color: '#b91c1c' }} />
          </IconButton>
        </Box>
      ))}
    </Stack>
  )
}

function NetworkPostCard({
  post,
  comments,
  feedPosts,
  onUpdate,
  onDelete,
  onOpenProfile,
  onToggleLike,
  onToggleDislike,
  onToggleCommentLike,
  onToggleCommentDislike,
  onReportPost,
  onReportComment,
  onLoadComments,
  onAddComment,
  onDeleteComment,
  onVotePoll,
  onBlockAuthor,
  allowUserBlock,
}: {
  post: PharmacyPostDto
  comments?: PharmacyPostCommentDto[]
  feedPosts: PharmacyPostDto[]
  onUpdate: (postId: string, body: string) => Promise<PharmacyPostDto | null>
  onDelete: (postId: string) => Promise<void>
  onOpenProfile: (profile: NetworkPharmacyProfile) => void
  onToggleLike: (postId: string) => Promise<void>
  onToggleDislike: (postId: string) => Promise<void>
  onToggleCommentLike: (postId: string, commentId: string) => Promise<void>
  onToggleCommentDislike: (postId: string, commentId: string) => Promise<void>
  onReportPost: (postId: string, reason: string, note?: string) => Promise<boolean>
  onReportComment: (postId: string, commentId: string, reason: string, note?: string) => Promise<boolean>
  onLoadComments: (postId: string) => Promise<PharmacyPostCommentDto[]>
  onAddComment: (postId: string, body: string, parentId?: string | null, imageDataUrls?: string[]) => Promise<PharmacyPostCommentDto | null>
  onDeleteComment: (postId: string, commentId: string) => Promise<void>
  onVotePoll: (postId: string, optionId: string) => Promise<void>
  onBlockAuthor?: (tenantId: string, userId: string) => Promise<void>
  allowUserBlock?: boolean
}) {
  const theme = useTheme()
  const hataBildir = useHataBildirici()
  const { onayIste } = useOpasDialog()
  const overlayZ = chatPanelOverlayZ(theme)
  const [isEditing, setIsEditing] = useState(false)
  const [editDraft, setEditDraft] = useState(post.body)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [postLikeBusy, setPostLikeBusy] = useState(false)
  const [postDislikeBusy, setPostDislikeBusy] = useState(false)
  const [commentLikeBusy, setCommentLikeBusy] = useState<string | null>(null)
  const [commentDislikeBusy, setCommentDislikeBusy] = useState<string | null>(null)
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [commentsLoaded, setCommentsLoaded] = useState(false)
  const [composeAnchor, setComposeAnchor] = useState<string | null>(null)
  const [composeDraft, setComposeDraft] = useState('')
  const [composeImages, setComposeImages] = useState<string[]>([])
  const [composeSending, setComposeSending] = useState(false)
  const composeImagesFileRef = useRef<HTMLInputElement | null>(null)
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null)
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null)
  const [reportTarget, setReportTarget] = useState<{ kind: 'post' } | { kind: 'comment'; commentId: string } | null>(null)
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [reportSending, setReportSending] = useState(false)
  const [pollVoteBusy, setPollVoteBusy] = useState(false)
  const composeInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (!isEditing) setEditDraft(post.body)
  }, [post.body, isEditing])

  const pharmacyName = post.pharmacyDisplayName?.trim() || `GLN ${post.authorGln}`
  const username = post.authorUsername?.trim() || pharmacyName
  const location = [post.pharmacyTown, post.pharmacyCity].filter(Boolean).join(', ')
  const roleSuffix = post.authorUserType === 'tenant' ? 'Eczacı' : null

  const commentTree = useMemo(() => buildCommentTree(comments ?? []), [comments])

  const ensureCommentsLoaded = async () => {
    if (commentsLoaded) return
    setCommentsLoading(true)
    try {
      await onLoadComments(post.id)
      setCommentsLoaded(true)
    } catch (e) {
      hataBildir(e, 'Yorumlar yüklenemedi')
      throw e
    } finally {
      setCommentsLoading(false)
    }
  }

  const openComposer = async (anchor: string) => {
    try {
      setCommentsOpen(true)
      await ensureCommentsLoaded()
      setComposeAnchor(anchor)
      setComposeDraft('')
      setComposeImages([])
      setTimeout(() => composeInputRef.current?.focus(), 0)
    } catch {
      /* ensureCommentsLoaded alert */
    }
  }

  const handlePostCommentClick = () => void openComposer('post')

  const handleSave = async () => {
    const text = editDraft.trim()
    if (!text || saving) return
    setSaving(true)
    try {
      await onUpdate(post.id, text)
      setIsEditing(false)
    } catch (e) {
      hataBildir(e, 'Güncellenemedi')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (deleting) return
    setDeleting(true)
    try {
      await onDelete(post.id)
      setConfirmDelete(false)
    } catch (e) {
      hataBildir(e, 'Silinemedi')
    } finally {
      setDeleting(false)
    }
  }

  const handleTogglePostLike = async () => {
    if (postLikeBusy) return
    setPostLikeBusy(true)
    try {
      await onToggleLike(post.id)
    } catch (e) {
      hataBildir(e, 'Beğeni işlemi başarısız')
    } finally {
      setPostLikeBusy(false)
    }
  }

  const handleToggleCommentLike = async (commentId: string) => {
    if (commentLikeBusy) return
    setCommentLikeBusy(commentId)
    try {
      await onToggleCommentLike(post.id, commentId)
    } catch (e) {
      hataBildir(e, 'Beğeni işlemi başarısız')
    } finally {
      setCommentLikeBusy(null)
    }
  }

  const handleTogglePostDislike = async () => {
    if (postDislikeBusy) return
    setPostDislikeBusy(true)
    try {
      await onToggleDislike(post.id)
    } catch (e) {
      hataBildir(e, 'Beğenmeme işlemi başarısız')
    } finally {
      setPostDislikeBusy(false)
    }
  }

  const handleToggleCommentDislike = async (commentId: string) => {
    if (commentDislikeBusy) return
    setCommentDislikeBusy(commentId)
    try {
      await onToggleCommentDislike(post.id, commentId)
    } catch (e) {
      hataBildir(e, 'Beğenmeme işlemi başarısız')
    } finally {
      setCommentDislikeBusy(null)
    }
  }

  const openActionMenu = (event: MouseEvent<HTMLElement>, target: { kind: 'post' } | { kind: 'comment'; commentId: string }) => {
    event.stopPropagation()
    setReportTarget(target)
    setActionMenuAnchor(event.currentTarget)
  }

  const closeActionMenu = () => setActionMenuAnchor(null)

  const engellemeOnayiIste = (ad: string, tenantId: string, userId: string) => {
    onayIste({
      tur: 'uyari',
      baslik: `${ad} engellensin mi?`,
      mesaj: 'Engellediğinizde birbirinizin paylaşım ve yorumlarını göremezsiniz.',
      onayEtiketi: 'Engelle',
      onOnay: () => {
        void (async () => {
          try {
            await onBlockAuthor?.(tenantId, userId)
          } catch (e) {
            hataBildir(e, 'Engellenemedi')
          }
        })()
      },
    })
  }

  const handleBlockAuthor = () => {
    if (!onBlockAuthor || !allowUserBlock || post.isOwnPost) return
    closeActionMenu()
    const name = post.authorUsername?.trim() || post.pharmacyDisplayName?.trim() || 'Bu kullanıcı'
    engellemeOnayiIste(name, post.tenantId, post.authorUserId)
  }

  const handleBlockCommentAuthor = (comment: PharmacyPostCommentDto) => {
    if (!onBlockAuthor || !allowUserBlock || comment.isOwnComment) return
    const name = comment.authorUsername?.trim() || comment.pharmacyDisplayName?.trim() || 'Bu kullanıcı'
    engellemeOnayiIste(name, comment.tenantId, comment.authorUserId)
  }

  const openReportDialog = () => {
    closeActionMenu()
    setReportDialogOpen(true)
  }

  const submitReport = async (reason: string, note: string) => {
    if (!reportTarget || reportSending) return
    setReportSending(true)
    try {
      const already =
        reportTarget.kind === 'post'
          ? await onReportPost(post.id, reason, note || undefined)
          : await onReportComment(post.id, reportTarget.commentId, reason, note || undefined)
      setReportDialogOpen(false)
      setReportTarget(null)
      if (already) {
        emitAppToast({
          type: 'info',
          title: 'Zaten bildirdiniz',
          message: 'Bu içerik için daha önce bildirim yapmıştınız.',
          duration: 4500,
        })
      } else {
        emitAppToast({
          type: 'success',
          title: 'Bildiriminiz alındı',
          message: 'İnceleme sonrası gerekli işlem yapılacaktır. Sonucu Destek sekmesinden takip edebilirsiniz.',
          duration: 4500,
        })
      }
    } catch (e) {
      emitAppToast({
        type: 'error',
        title: 'Bildirim gönderilemedi',
        message: e instanceof Error ? e.message : 'Lütfen tekrar deneyin.',
        duration: 5000,
      })
    } finally {
      setReportSending(false)
    }
  }

  const submitCompose = async () => {
    const text = composeDraft.trim()
    const images = composeImages
    if ((!text && images.length === 0) || composeSending || !composeAnchor) return
    setComposeSending(true)
    const saved = text
    const savedImages = images
    setComposeDraft('')
    setComposeImages([])
    const parentId = composeAnchor === 'post' ? null : composeAnchor
    try {
      await onAddComment(post.id, saved, parentId, savedImages.length > 0 ? savedImages : undefined)
      setCommentsOpen(true)
      setCommentsLoaded(true)
      setComposeAnchor(null)
    } catch (e) {
      setComposeDraft(saved)
      setComposeImages(savedImages)
      hataBildir(e, 'Yorum gönderilemedi')
    } finally {
      setComposeSending(false)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (deletingCommentId) return
    setDeletingCommentId(commentId)
    try {
      await onDeleteComment(post.id, commentId)
      if (composeAnchor === commentId) setComposeAnchor(null)
    } catch (e) {
      hataBildir(e, 'Yorum silinemedi')
    } finally {
      setDeletingCommentId(null)
    }
  }

  const handleVotePoll = async (optionId: string) => {
    if (pollVoteBusy || !post.poll || post.poll.votedOptionId || post.poll.isClosed) return
    setPollVoteBusy(true)
    try {
      await onVotePoll(post.id, optionId)
    } catch (e) {
      hataBildir(e, 'Oy kullanılamadı')
    } finally {
      setPollVoteBusy(false)
    }
  }

  const renderEngagementRow = (
    likeCount: number,
    likedByMe: boolean,
    dislikeCount: number,
    dislikedByMe: boolean,
    replyCount: number,
    onLike: () => void,
    onDislike: () => void,
    onComment: () => void,
    likeBusy: boolean,
    dislikeBusy: boolean,
    alwaysShowReplyCount: boolean,
    actionTarget: { kind: 'post' } | { kind: 'comment'; commentId: string }
  ) => (
    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ mt: 0.65, width: '100%' }}>
      <IconButton
        size="small"
        onClick={() => void onLike()}
        disabled={likeBusy}
        aria-label={likedByMe ? 'Beğeniyi kaldır' : 'Beğen'}
        sx={{ color: likedByMe ? '#e11d48' : pastel.textMuted }}
      >
        {likeBusy ? (
          <OpasBrandSpinner size={16} />
        ) : likedByMe ? (
          <FavoriteRoundedIcon sx={{ fontSize: 17 }} />
        ) : (
          <FavoriteBorderRoundedIcon sx={{ fontSize: 17 }} />
        )}
      </IconButton>
      <Typography className={outfit.className} sx={{ fontFamily: outfit.style.fontFamily, fontSize: '0.74rem', fontWeight: 700, color: pastel.textMuted, minWidth: 14 }}>
        {likeCount}
      </Typography>
      <IconButton
        size="small"
        onClick={() => void onDislike()}
        disabled={dislikeBusy}
        aria-label={dislikedByMe ? 'Beğenmemeyi kaldır' : 'Beğenme'}
        sx={{ color: dislikedByMe ? '#2563eb' : pastel.textMuted, ml: 0.15 }}
      >
        {dislikeBusy ? (
          <OpasBrandSpinner size={16} />
        ) : dislikedByMe ? (
          <ThumbDownAltIcon sx={{ fontSize: 17 }} />
        ) : (
          <ThumbDownOffAltOutlinedIcon sx={{ fontSize: 17 }} />
        )}
      </IconButton>
      <Typography className={outfit.className} sx={{ fontFamily: outfit.style.fontFamily, fontSize: '0.74rem', fontWeight: 700, color: pastel.textMuted, minWidth: 14 }}>
        {dislikeCount}
      </Typography>
      <IconButton size="small" onClick={() => void onComment()} aria-label="Yorum yap" sx={{ color: pastel.textMuted, ml: 0.35 }}>
        <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      {(alwaysShowReplyCount || replyCount > 0) && (
        <Typography className={outfit.className} sx={{ fontFamily: outfit.style.fontFamily, fontSize: '0.74rem', fontWeight: 700, color: pastel.textMuted, minWidth: 14 }}>
          {replyCount}
        </Typography>
      )}
      <Box sx={{ flex: 1, minWidth: 8 }} />
      <IconButton
        size="small"
        aria-label="Bildir veya şikayet et"
        onClick={(e) => openActionMenu(e, actionTarget)}
        sx={{
          color: pastel.textMuted,
          width: 28,
          height: 28,
          ml: 0.25,
          transition: 'background-color 0.15s ease, color 0.15s ease',
          '&:hover': {
            bgcolor: 'rgba(196,181,253,0.22)',
            color: pastel.text,
          },
        }}
      >
        <MoreHorizRoundedIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </Stack>
  )

  const renderInlineComposer = (anchor: string, label: string, placeholder: string) => {
    if (composeAnchor !== anchor) return null
    return (
      <Box sx={{ mt: 0.75, pl: 0.25 }}>
        <Typography className={outfit.className} sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#6d28d9', mb: 0.45 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="flex-end">
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={3}
            inputRef={composeInputRef}
            placeholder={placeholder}
            value={composeDraft}
            onChange={(e) => setComposeDraft(e.target.value.slice(0, 2000))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void submitCompose()
              }
              if (e.key === 'Escape') setComposeAnchor(null)
            }}
            sx={{
              ...networkFieldSx,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                bgcolor: '#fff',
                fontFamily: outfit.style.fontFamily,
                fontSize: '0.78rem',
              },
            }}
          />
          <input
            ref={composeImagesFileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            hidden
            onChange={(e) => {
              void pickNetworkPostImages(e.target.files, composeImages, setComposeImages, hataBildir)
              e.target.value = ''
            }}
          />
          <IconButton
            size="small"
            aria-label="Görsel ekle"
            disabled={composeImages.length >= MAX_NETWORK_POST_IMAGES}
            onClick={() => composeImagesFileRef.current?.click()}
            sx={{ color: pastel.textMuted, mb: 0.15 }}
          >
            <ImageOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => void submitCompose()}
            disabled={(!composeDraft.trim() && composeImages.length === 0) || composeSending}
            aria-label="Gönder"
          >
            {composeSending ? <OpasBrandSpinner size={18} /> : <SendRoundedIcon sx={{ fontSize: 18, color: pastel.text }} />}
          </IconButton>
        </Stack>
        <NetworkComposeImagePreviews
          images={composeImages}
          onRemove={(idx) => setComposeImages((prev) => prev.filter((_, i) => i !== idx))}
        />
      </Box>
    )
  }

  const countReplies = (node: CommentNode): number => {
    let n = node.children.length
    for (const ch of node.children) n += countReplies(ch)
    return n
  }

  const openCommentProfile = (comment: PharmacyPostCommentDto) => {
    const tenantRef = feedPosts.find((p) => p.tenantId === comment.tenantId)
    onOpenProfile(profileFromComment(comment, tenantRef))
  }

  const renderCommentNode = (node: CommentNode, depth: number) => {
    const cPharmacyName = node.pharmacyDisplayName?.trim() || 'Eczane'
    const cName = node.authorUsername?.trim() || cPharmacyName
    const cLocation = [node.pharmacyTown, node.pharmacyCity].filter(Boolean).join(', ')
    const cRoleSuffix = node.authorUserType === 'tenant' ? 'Eczacı' : null
    const replyCount = countReplies(node)
    return (
      <Box key={node.id} sx={{ mb: 0.65, ml: depth * 1.15 }}>
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
          <NetworkProfileAvatar
            username={cName}
            tenantId={node.tenantId}
            userId={node.authorUserId}
            avatarUrl={node.authorAvatarUrl}
            size={28}
            onClick={() => openCommentProfile(node)}
            sx={{ mt: 0.15, flexShrink: 0, transition: 'transform 0.15s ease', '&:hover': { transform: 'scale(1.06)' } }}
          />
          <Box sx={{ flex: 1, minWidth: 0, py: 0.55, px: 0.75, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.58)', border: '1px solid rgba(196,181,253,0.22)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, alignItems: 'flex-start' }}>
              <Box
                sx={{ minWidth: 0, cursor: 'pointer' }}
                onClick={() => openCommentProfile(node)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openCommentProfile(node)
                  }
                }}
              >
                {node.replyToUsername && (
                  <Typography className={outfit.className} sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#7c3aed', mb: 0.1 }}>
                    @{node.replyToUsername}
                  </Typography>
                )}
                <Typography className={outfit.className} sx={{ fontWeight: 700, fontSize: '0.74rem', color: pastel.text, lineHeight: 1.25 }}>
                  {cName}
                  {cRoleSuffix && (
                    <Typography component="span" className={outfit.className} sx={{ fontWeight: 500, fontSize: '0.62rem', color: pastel.textMuted, ml: 0.5 }}>
                      · {cRoleSuffix}
                    </Typography>
                  )}
                </Typography>
                <Typography className={outfit.className} sx={{ fontWeight: 600, fontSize: '0.66rem', color: pastel.textMuted, lineHeight: 1.3 }}>
                  {cPharmacyName}
                </Typography>
                <Typography className={outfit.className} sx={{ fontSize: '0.58rem', color: pastel.textMuted, mt: 0.1, fontWeight: 400 }}>
                  {cLocation ? `${cLocation} · ` : ''}
                  {formatRelativeTime(node.createdAtUtc)}
                </Typography>
              </Box>
              {node.canDelete && (
                <IconButton
                  size="small"
                  aria-label={node.isSuperAdminModerator && !node.isOwnComment ? 'SuperAdmin moderasyon — yorumu kaldır' : 'Yorumu sil'}
                  disabled={deletingCommentId === node.id}
                  onClick={() => void handleDeleteComment(node.id)}
                  sx={{ mt: -0.25 }}
                >
                  {deletingCommentId === node.id ? <OpasBrandSpinner size={14} /> : <DeleteOutlineIcon sx={{ fontSize: 14, color: pastel.textMuted }} />}
                </IconButton>
              )}
            </Box>
            {node.body ? (
              <Typography className={outfit.className} sx={{ fontSize: '0.78rem', color: pastel.text, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.2 }}>
                {node.body}
              </Typography>
            ) : null}
            <NetworkPostImages images={node.images} compact />
            {renderEngagementRow(
              node.likeCount,
              node.likedByMe,
              node.dislikeCount,
              node.dislikedByMe,
              replyCount,
              () => void handleToggleCommentLike(node.id),
              () => void handleToggleCommentDislike(node.id),
              () => void openComposer(node.id),
              commentLikeBusy === node.id,
              commentDislikeBusy === node.id,
              false,
              { kind: 'comment', commentId: node.id }
            )}
            {renderInlineComposer(node.id, `@${cName} yanıtı`, `@${cName} — yanıtınızı yazın…`)}
          </Box>
        </Box>
        {node.children.map((child) => renderCommentNode(child, depth + 1))}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        mb: 1.25,
        p: 1.35,
        borderRadius: 2,
        bgcolor: isEditing ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0.82)',
        border: isEditing ? `1.5px solid ${pastel.lavender}` : '1px solid rgba(196, 181, 253, 0.28)',
        boxShadow: isEditing ? '0 4px 16px rgba(91, 71, 120, 0.1)' : '0 2px 12px rgba(91, 71, 120, 0.06)',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.1, alignItems: 'flex-start', fontFamily: outfit.style.fontFamily }}>
        <NetworkProfileAvatar
          username={username}
          tenantId={post.tenantId}
          userId={post.authorUserId}
          avatarUrl={post.authorAvatarUrl}
          size={36}
          onClick={() => onOpenProfile(profileFromPost(post))}
          sx={{
            fontSize: '0.75rem',
            transition: 'transform 0.15s ease',
            '&:hover': { transform: 'scale(1.06)' },
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
            <Box
              sx={{ minWidth: 0, cursor: 'pointer' }}
              onClick={() => onOpenProfile(profileFromPost(post))}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onOpenProfile(profileFromPost(post))
                }
              }}
            >
              <Typography className={outfit.className} sx={{ fontWeight: 800, fontSize: '0.88rem', color: pastel.text, lineHeight: 1.25 }}>
                {username}
                {roleSuffix && (
                  <Typography
                    component="span"
                    className={outfit.className}
                    sx={{ fontWeight: 500, fontSize: '0.72rem', color: pastel.textMuted, ml: 0.75 }}
                  >
                    · {roleSuffix}
                  </Typography>
                )}
              </Typography>
              <Typography className={outfit.className} sx={{ fontWeight: 600, fontSize: '0.76rem', color: pastel.textMuted, lineHeight: 1.3 }}>
                {pharmacyName}
              </Typography>
              <Typography className={outfit.className} sx={{ fontSize: '0.68rem', color: pastel.textMuted, mt: 0.2, fontWeight: 400 }}>
                {location ? `${location} · ` : ''}
                {formatRelativeTime(post.createdAtUtc)}
                {post.wasEdited ? ' · düzenlendi' : ''}
              </Typography>
            </Box>
            {!isEditing && (post.canEdit || post.canDelete) && !confirmDelete && (
              <Box sx={{ display: 'flex', flexShrink: 0 }}>
                {post.canEdit && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditDraft(post.body)
                      setIsEditing(true)
                    }}
                    aria-label="Düzenle"
                  >
                    <EditOutlinedIcon sx={{ fontSize: 16, color: pastel.textMuted }} />
                  </IconButton>
                )}
                {post.canDelete && (
                  <IconButton size="small" onClick={() => setConfirmDelete(true)} aria-label="Sil">
                    <DeleteOutlineIcon sx={{ fontSize: 16, color: '#b91c1c' }} />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>

          {confirmDelete ? (
            <Box sx={{ mt: 1, p: 1, borderRadius: 1.5, bgcolor: 'rgba(254, 226, 226, 0.5)', border: '1px solid rgba(248, 113, 113, 0.35)' }}>
              <Typography className={outfit.className} sx={{ fontSize: '0.8rem', color: pastel.text, mb: 1 }}>
                {post.isSuperAdminModerator && !post.isOwnPost
                  ? 'Bu paylaşımı SuperAdmin olarak kaldırmak istediğinize emin misiniz?'
                  : 'Bu paylaşım silinsin mi?'}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => setConfirmDelete(false)} disabled={deleting} sx={{ textTransform: 'none' }}>
                  Vazgeç
                </Button>
                <Button size="small" color="error" variant="contained" onClick={() => void handleDelete()} disabled={deleting} sx={{ textTransform: 'none', minWidth: 72 }}>
                  {deleting ? <OpasBrandSpinner size={16} color="#fff" /> : 'Sil'}
                </Button>
              </Stack>
            </Box>
          ) : isEditing ? (
            <Box sx={{ mt: 0.85 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={8}
                size="small"
                autoFocus
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value.slice(0, 4000))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    void handleSave()
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    setIsEditing(false)
                    setEditDraft(post.body)
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    fontFamily: outfit.style.fontFamily,
                    fontSize: '0.84rem',
                    bgcolor: '#fff',
                  },
                  '& .MuiOutlinedInput-input': { color: pastel.text },
                }}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.75 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setIsEditing(false)
                    setEditDraft(post.body)
                  }}
                  disabled={saving}
                  sx={{ textTransform: 'none', color: pastel.textMuted }}
                >
                  Vazgeç
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => void handleSave()}
                  disabled={!editDraft.trim() || saving}
                  sx={{ textTransform: 'none', minWidth: 80 }}
                >
                  {saving ? <OpasBrandSpinner size={16} color="#fff" /> : 'Kaydet'}
                </Button>
              </Stack>
            </Box>
          ) : (
            <>
              {post.body ? (
                <Typography
                  className={outfit.className}
                  sx={{
                    mt: 0.85,
                    fontSize: '0.84rem',
                    lineHeight: 1.5,
                    color: pastel.text,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {post.body}
                </Typography>
              ) : null}
              {!isEditing && !confirmDelete && post.images && post.images.length > 0 && (
                <NetworkPostImages images={post.images} />
              )}
            </>
          )}

          {!isEditing && !confirmDelete && post.poll && (
            <NetworkPollBlock poll={post.poll} disabled={pollVoteBusy} onVote={(id) => void handleVotePoll(id)} />
          )}

          {!isEditing && !confirmDelete && (
            <>
              {renderEngagementRow(
                post.likeCount,
                post.likedByMe,
                post.dislikeCount,
                post.dislikedByMe,
                post.commentCount,
                () => void handleTogglePostLike(),
                () => void handleTogglePostDislike(),
                () => void handlePostCommentClick(),
                postLikeBusy,
                postDislikeBusy,
                true,
                { kind: 'post' }
              )}
              {renderInlineComposer('post', 'Paylaşıma yorum', `${username} paylaşımına yorum yazın…`)}
            </>
          )}

          {commentsOpen && !isEditing && !confirmDelete && (
            <Box
              sx={{
                mt: 1.1,
                pt: 1,
                pl: 0.25,
                ml: 0.65,
                borderTop: `1px solid rgba(196,181,253,0.22)`,
              }}
            >
              {commentsLoading ? (
                <OpasBrandLoading size={28} sx={{ py: 1.5 }} />
              ) : commentTree.length === 0 ? (
                composeAnchor !== 'post' && (
                  <Typography className={outfit.className} sx={{ fontSize: '0.75rem', color: pastel.textMuted, mb: 0.75, pl: 0.25 }}>
                    Henüz yorum yok.
                  </Typography>
                )
              ) : (
                commentTree.map((node) => renderCommentNode(node, 0))
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeActionMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: overlayZ }}
        disableScrollLock
        slotProps={{
          paper: { sx: networkActionMenuPaperSx },
          list: { dense: true },
        }}
      >
        {allowUserBlock && reportTarget?.kind === 'post' && !post.isOwnPost && (
          <MenuItem onClick={handleBlockAuthor} sx={networkActionMenuItemSx}>
            <BlockOutlinedIcon />
            Engelle
          </MenuItem>
        )}
        {allowUserBlock && reportTarget?.kind === 'comment' && (() => {
          const c = comments?.find((x) => x.id === reportTarget.commentId)
          return c && !c.isOwnComment
        })() && (
          <MenuItem
            onClick={() => {
              const c = comments?.find((x) => x.id === reportTarget.commentId)
              closeActionMenu()
              if (c && !c.isOwnComment) handleBlockCommentAuthor(c)
            }}
            sx={networkActionMenuItemSx}
          >
            <BlockOutlinedIcon />
            Engelle
          </MenuItem>
        )}
        <MenuItem onClick={openReportDialog} sx={networkActionMenuItemSx}>
          <FlagOutlinedIcon />
          Şikayet et
        </MenuItem>
      </Menu>
      <NetworkReportDialog
        open={reportDialogOpen}
        onClose={() => {
          if (!reportSending) {
            setReportDialogOpen(false)
            setReportTarget(null)
          }
        }}
        onSubmit={submitReport}
        submitting={reportSending}
      />
    </Box>
  )
}

function NetworkModerationPanel({
  enabled,
  onQueueChanged,
}: {
  enabled: boolean
  onQueueChanged?: () => void
}) {
  const { onayIste } = useOpasDialog()
  const { tickets, loading, error, loadTickets, resolveReport, completeTicket } = usePharmacyNetworkModeration(enabled)
  const { bans, loading: bansLoading, liftBan } = usePharmacyNetworkBans(enabled)
  const [queueTab, setQueueTab] = useState<SupportQueueTab>('pending')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [busyBanId, setBusyBanId] = useState<string | null>(null)
  const [warnTarget, setWarnTarget] = useState<NetworkModerationReportDto | null>(null)
  const [warnText, setWarnText] = useState('')
  const [reporterReply, setReporterReply] = useState('')
  const [completeTarget, setCompleteTarget] = useState<NetworkModerationTicketDto | null>(null)
  const [completeMessage, setCompleteMessage] = useState('')

  useEffect(() => {
    if (enabled) void loadTickets(queueTab)
  }, [enabled, queueTab, loadTickets])

  const runReportAction = async (
    report: NetworkModerationReportDto,
    action: 'delete' | 'warn' | 'ban' | 'dismiss',
    note?: string,
    warningMessage?: string,
    reporterMessage?: string
  ) => {
    setBusyId(report.id)
    try {
      await resolveReport(report.id, action, note, warningMessage, reporterMessage)
      onQueueChanged?.()
      void loadTickets(queueTab)
      if (warnTarget?.id === report.id) {
        setWarnTarget(null)
        setWarnText('')
        setReporterReply('')
      }
      emitAppToast({ type: 'success', title: 'Tamamlandı', message: 'Talep sonuçlandırıldı; bildiren eczaneye iletildi.' })
    } catch (e) {
      emitAppToast({ type: 'error', title: 'İşlem başarısız', message: e instanceof Error ? e.message : 'İşlem tamamlanamadı' })
    } finally {
      setBusyId(null)
    }
  }

  const runComplete = async () => {
    if (!completeTarget) return
    setBusyId(completeTarget.id)
    try {
      await completeTicket(completeTarget.id, completeMessage.trim())
      onQueueChanged?.()
      void loadTickets(queueTab)
      setCompleteTarget(null)
      setCompleteMessage('')
      emitAppToast({ type: 'success', title: 'Tamamlandı', message: 'Destek talebi sonuçlandırıldı.' })
    } catch (e) {
      emitAppToast({ type: 'error', title: 'İşlem başarısız', message: e instanceof Error ? e.message : 'İşlem tamamlanamadı' })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, fontFamily: outfit.style.fontFamily }}>
      <Tabs
        value={queueTab}
        onChange={(_, v) => setQueueTab(v)}
        variant="fullWidth"
        sx={supportTabsSx}
      >
        <Tab value="pending" label="Bekleyenler" />
        <Tab value="resolved" label="Cevaplananlar" />
      </Tabs>

      {loading && tickets.length === 0 ? (
        <OpasBrandLoading label="Destek bildirimleri yükleniyor…" sx={{ minHeight: 120, flex: 1 }} />
      ) : (
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
          {error && (
            <Typography className={outfit.className} sx={{ fontSize: '0.8rem', color: '#b91c1c', mb: 1 }}>
              {error}
            </Typography>
          )}

          {queueTab === 'pending' && bans.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography className={outfit.className} sx={{ fontSize: '0.72rem', fontWeight: 700, color: pastel.textMuted, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Eczaneler arası erişimi kısıtlananlar
              </Typography>
              {bans.map((b) => (
                <Paper key={b.id} elevation={0} sx={{ mb: 0.75, p: 1, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.88)', border: '1px solid rgba(251,191,36,0.35)' }}>
                  <Typography className={outfit.className} sx={{ fontSize: '0.76rem', color: pastel.text }}>
                    {b.tenantId.slice(0, 8)}… / {b.userId.slice(0, 8)}…
                  </Typography>
                  {b.reason && (
                    <Typography className={outfit.className} sx={{ fontSize: '0.72rem', color: pastel.textMuted, mt: 0.25 }}>
                      {b.reason}
                    </Typography>
                  )}
                  <Button
                    size="small"
                    disabled={busyBanId === b.id || bansLoading}
                    onClick={() => {
                      onayIste({
                        baslik: 'Kısıtlama kaldırılsın mı?',
                        mesaj: 'Bu kullanıcı eczaneler arası paylaşımlara yeniden erişebilecek.',
                        onayEtiketi: 'Kaldır',
                        onOnay: () => {
                          setBusyBanId(b.id)
                          void liftBan(b.id)
                            .catch((e) => emitAppToast({ type: 'error', title: 'Kaldırılamadı', message: e instanceof Error ? e.message : 'Kaldırılamadı' }))
                            .finally(() => setBusyBanId(null))
                        },
                      })
                    }}
                    sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.72rem' }}
                  >
                    Kısıtlamayı kaldır
                  </Button>
                </Paper>
              ))}
            </Box>
          )}

          {tickets.length === 0 && !loading && (
            <Typography className={outfit.className} sx={{ fontSize: '0.85rem', color: pastel.textMuted, py: 2 }}>
              {queueTab === 'pending' ? 'Bekleyen talep yok.' : 'Cevaplanan talep yok.'}
            </Typography>
          )}

          {tickets.map((ticket) => {
            const r = ticket.report
            if (ticket.kind === 'network_report' && r) {
              const preview = r.commentId ? r.commentBody || '(yorum silinmiş)' : r.postBody || '(paylaşım silinmiş)'
              const author = r.commentId ? r.commentAuthorUsername || 'Yorum sahibi' : r.postPharmacyDisplayName || 'Eczane'
              return (
                <Paper key={ticket.id} elevation={0} sx={{ mb: 1, p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.88)', border: '1px solid rgba(196,181,253,0.32)' }}>
                  <Typography className={outfit.className} sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#6d28d9' }}>
                    {ticket.subject}
                  </Typography>
                  <Typography className={outfit.className} sx={{ fontSize: '0.72rem', color: pastel.textMuted, mt: 0.25 }}>
                    {formatRelativeTime(ticket.createdAtUtc)}
                  </Typography>
                  {queueTab === 'pending' && (
                    <>
                      <Typography className={outfit.className} sx={{ fontSize: '0.78rem', fontWeight: 700, color: pastel.text, mt: 0.35 }}>
                        {author}
                      </Typography>
                      <Typography className={outfit.className} sx={{ fontSize: '0.76rem', color: pastel.text, mt: 0.35, whiteSpace: 'pre-wrap' }}>
                        {preview}
                      </Typography>
                      {r.note && (
                        <Typography className={outfit.className} sx={{ fontSize: '0.72rem', color: pastel.textMuted, mt: 0.35 }}>
                          Bildiren notu: {r.note}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                        <Button size="small" color="error" variant="outlined" disabled={busyId === r.id} onClick={() => void runReportAction(r, 'delete', undefined, undefined, reporterReply.trim() || undefined)} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>
                          Kaldır
                        </Button>
                        <Button size="small" disabled={busyId === r.id} onClick={() => { setWarnTarget(r); setWarnText('Eczaneler arası kurallara uymanız gerekmektedir.') }} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>
                          Uyar
                        </Button>
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          disabled={busyId === r.id}
                          onClick={() => {
                            const reporterMessage = reporterReply.trim() || undefined
                            onayIste({
                              tur: 'uyari',
                              baslik: 'Erişim kısıtlansın mı?',
                              mesaj: 'Bu kullanıcı eczaneler arası paylaşımlara erişemeyecek.',
                              onayEtiketi: 'Kısıtla',
                              onOnay: () => { void runReportAction(r, 'ban', undefined, undefined, reporterMessage) },
                            })
                          }}
                          sx={{ textTransform: 'none', fontSize: '0.72rem' }}
                        >
                          Ban
                        </Button>
                        <Button size="small" disabled={busyId === r.id} onClick={() => void runReportAction(r, 'dismiss', undefined, undefined, reporterReply.trim() || undefined)} sx={{ textTransform: 'none', fontSize: '0.72rem', color: pastel.textMuted }}>
                          Reddet
                        </Button>
                      </Stack>
                    </>
                  )}
                  {queueTab === 'resolved' && ticket.resolutionMessage && (
                    <Typography className={outfit.className} sx={{ fontSize: '0.76rem', color: pastel.text, mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {ticket.resolutionMessage}
                    </Typography>
                  )}
                </Paper>
              )
            }

            return (
              <Paper key={ticket.id} elevation={0} sx={{ mb: 1, p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.88)', border: '1px solid rgba(196,181,253,0.32)' }}>
                <Typography className={outfit.className} sx={{ fontSize: '0.78rem', fontWeight: 700, color: pastel.text }}>
                  {ticket.subject}
                </Typography>
                <Typography className={outfit.className} sx={{ fontSize: '0.72rem', color: pastel.textMuted, mt: 0.25 }}>
                  {formatRelativeTime(ticket.createdAtUtc)}
                </Typography>
                {ticket.body && (
                  <Typography className={outfit.className} sx={{ fontSize: '0.76rem', color: pastel.text, mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {ticket.body}
                  </Typography>
                )}
                {queueTab === 'pending' && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={busyId === ticket.id}
                    onClick={() => { setCompleteTarget(ticket); setCompleteMessage('Talebiniz incelendi ve sonuçlandırıldı.') }}
                    sx={{ ...chatPanelModalPrimaryBtnSx, mt: 1, fontSize: '0.72rem', minWidth: 'auto', px: 1.5, py: 0.5 }}
                  >
                    Tamamlandı
                  </Button>
                )}
                {queueTab === 'resolved' && ticket.resolutionMessage && (
                  <Box sx={{ mt: 1, p: 1, borderRadius: 1.5, bgcolor: 'rgba(209,250,229,0.45)', border: '1px solid rgba(52,211,153,0.35)' }}>
                    <Typography className={outfit.className} sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#047857', mb: 0.25 }}>
                      OPAS yanıtı
                    </Typography>
                    <Typography className={outfit.className} sx={{ fontSize: '0.76rem', color: pastel.text, whiteSpace: 'pre-wrap' }}>
                      {ticket.resolutionMessage}
                    </Typography>
                  </Box>
                )}
              </Paper>
            )
          })}
        </Box>
      )}

      <ChatPanelModal
        open={Boolean(warnTarget)}
        onClose={() => !busyId && setWarnTarget(null)}
        title="Uyarı gönder"
        subtitle="Hedef kullanıcıya uyarı iletilecek; bildiren eczaneye de sonuç mesajı gönderilir."
        actions={
          <>
            <Button onClick={() => setWarnTarget(null)} disabled={Boolean(busyId)} sx={chatPanelModalGhostBtnSx}>
              Vazgeç
            </Button>
            <Button
              variant="contained"
              disabled={!warnText.trim() || Boolean(busyId) || !warnTarget}
              onClick={() => warnTarget && void runReportAction(warnTarget, 'warn', undefined, warnText.trim(), reporterReply.trim() || undefined)}
              sx={chatPanelModalPrimaryBtnSx}
            >
              {busyId ? <OpasBrandSpinner size={16} color="#fff" /> : 'Gönder'}
            </Button>
          </>
        }
      >
        <Stack spacing={1.5}>
          <TextField fullWidth multiline minRows={3} maxRows={6} label="Uyarı metni" value={warnText} onChange={(e) => setWarnText(e.target.value.slice(0, 500))} sx={networkFieldSx} />
          <TextField fullWidth multiline minRows={2} maxRows={4} label="Bildirene iletilecek mesaj (isteğe bağlı)" value={reporterReply} onChange={(e) => setReporterReply(e.target.value.slice(0, 500))} placeholder="Boş bırakırsanız standart mesaj gönderilir" sx={networkFieldSx} />
        </Stack>
      </ChatPanelModal>

      <ChatPanelModal
        open={Boolean(completeTarget)}
        onClose={() => !busyId && setCompleteTarget(null)}
        title="Talebi tamamla"
        subtitle="Eczaneye iletilecek cevap metnini yazın."
        actions={
          <>
            <Button onClick={() => setCompleteTarget(null)} disabled={Boolean(busyId)} sx={chatPanelModalGhostBtnSx}>
              Vazgeç
            </Button>
            <Button variant="contained" disabled={!completeMessage.trim() || Boolean(busyId)} onClick={() => void runComplete()} sx={chatPanelModalPrimaryBtnSx}>
              {busyId ? <OpasBrandSpinner size={16} color="#fff" /> : 'Tamamlandı'}
            </Button>
          </>
        }
      >
        <TextField fullWidth multiline minRows={4} maxRows={8} label="Cevap metni" value={completeMessage} onChange={(e) => setCompleteMessage(e.target.value.slice(0, 500))} sx={networkFieldSx} />
      </ChatPanelModal>
    </Box>
  )
}

function TenantSupportChannel({
  enabled,
  onQueueChanged,
}: {
  enabled: boolean
  onQueueChanged?: () => void
}) {
  const { tickets, loading, error, loadTickets, createTicket, markSeen } = usePharmacyNetworkSupport(enabled)
  const [queueTab, setQueueTab] = useState<SupportQueueTab>('pending')
  const [selected, setSelected] = useState<SupportTicketDto | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [newSubject, setNewSubject] = useState('')
  const [newBody, setNewBody] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (enabled) void loadTickets(queueTab)
  }, [enabled, queueTab, loadTickets])

  useEffect(() => {
    const reload = () => {
      if (!enabled) return
      void loadTickets(queueTab)
      onQueueChanged?.()
    }
    document.addEventListener('opas-support-ticket-resolved', reload)
    return () => document.removeEventListener('opas-support-ticket-resolved', reload)
  }, [enabled, queueTab, loadTickets, onQueueChanged])

  useEffect(() => {
    if (!enabled) {
      setSelected(null)
      setNewOpen(false)
    }
  }, [enabled])

  const openTicket = (ticket: SupportTicketDto) => {
    setSelected(ticket)
    if (ticket.status === 'resolved' && !ticket.userSeenAtUtc) {
      void markSeen(ticket.id).then(() => onQueueChanged?.())
    }
  }

  const submitNew = async () => {
    setCreating(true)
    try {
      await createTicket(newSubject.trim(), newBody.trim())
      setNewOpen(false)
      setNewSubject('')
      setNewBody('')
      setQueueTab('pending')
      emitAppToast({ type: 'success', title: 'Talep gönderildi', message: 'Destek talebiniz alındı; yanıt gelince bildirim alacaksınız.' })
    } catch (e) {
      emitAppToast({ type: 'error', title: 'Gönderilemedi', message: e instanceof Error ? e.message : 'Talep oluşturulamadı' })
    } finally {
      setCreating(false)
    }
  }

  if (selected) {
    return (
      <Fade in timeout={320}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1.5, pb: 1 }}>
            <IconButton size="small" onClick={() => setSelected(null)} sx={{ color: pastel.textMuted }}>
              <ArrowBackRoundedIcon fontSize="small" />
            </IconButton>
            <Typography className={outfit.className} sx={{ fontWeight: 700, color: pastel.text, fontSize: '0.95rem', flex: 1 }}>
              {selected.subject}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
            <Typography className={outfit.className} sx={{ fontSize: '0.72rem', color: pastel.textMuted, mb: 1 }}>
              {formatRelativeTime(selected.createdAtUtc)}
              {selected.status === 'pending' ? ' · Bekliyor' : ' · Cevaplandı'}
            </Typography>
            {selected.body && (
              <Typography className={outfit.className} sx={{ fontSize: '0.85rem', color: pastel.text, whiteSpace: 'pre-wrap', mb: 1.5 }}>
                {selected.body}
              </Typography>
            )}
            {selected.resolutionMessage && (
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(209,250,229,0.45)', border: '1px solid rgba(52,211,153,0.35)' }}>
                <Typography className={outfit.className} sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#047857', mb: 0.5 }}>
                  OPAS yanıtı
                </Typography>
                <Typography className={outfit.className} sx={{ fontSize: '0.85rem', color: pastel.text, whiteSpace: 'pre-wrap' }}>
                  {selected.resolutionMessage}
                </Typography>
              </Box>
            )}
            {selected.status === 'pending' && selected.kind === 'network_report' && (
              <Typography className={outfit.className} sx={{ fontSize: '0.8rem', color: pastel.textMuted, mt: 2 }}>
                Şikayetiniz inceleniyor. Sonuçlandığında burada ve bildirimlerde göreceksiniz.
              </Typography>
            )}
          </Box>
        </Box>
      </Fade>
    )
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Tabs value={queueTab} onChange={(_, v) => setQueueTab(v)} variant="fullWidth" sx={supportTabsSx}>
        <Tab value="pending" label="Bekleyenler" />
        <Tab value="resolved" label="Cevaplananlar" />
      </Tabs>

      {queueTab === 'pending' && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Button size="small" variant="outlined" onClick={() => setNewOpen(true)} sx={{ textTransform: 'none', fontSize: '0.78rem', borderRadius: 2 }}>
            Yeni destek talebi
          </Button>
        </Box>
      )}

      {loading && tickets.length === 0 ? (
        <OpasBrandLoading label="Destek talepleriniz yükleniyor…" sx={{ minHeight: 120, flex: 1 }} />
      ) : (
        <Fade in timeout={400}>
          <List sx={{ py: 1, px: 1, flex: 1, overflow: 'auto' }}>
            {error && (
              <Typography className={outfit.className} sx={{ fontSize: '0.8rem', color: '#b91c1c', px: 1, mb: 1 }}>
                {error}
              </Typography>
            )}
            {tickets.length === 0 && !loading && (
              <Typography className={outfit.className} sx={{ fontSize: '0.85rem', color: pastel.textMuted, px: 1.5, py: 2 }}>
                {queueTab === 'pending' ? 'Bekleyen talebiniz yok.' : 'Cevaplanan talep yok.'}
              </Typography>
            )}
            {tickets.map((ticket) => (
              <ListItemButton
                key={ticket.id}
                onClick={() => openTicket(ticket)}
                sx={{
                  borderRadius: 2,
                  mb: 0.75,
                  py: 1.25,
                  px: 1.5,
                  bgcolor: ticket.status === 'resolved' && !ticket.userSeenAtUtc ? 'rgba(209,250,229,0.35)' : 'rgba(255,255,255,0.55)',
                  border: '1px solid rgba(196, 181, 253, 0.25)',
                  '&:hover': { bgcolor: '#fff', transform: 'translateX(-4px)', boxShadow: '0 8px 24px rgba(91, 71, 120, 0.08)' },
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: ticket.kind === 'network_report' ? '#ddd6fe' : '#bfdbfe', color: pastel.text, fontWeight: 700, fontSize: '0.85rem', fontFamily: outfit.style.fontFamily }}>
                    {ticket.kind === 'network_report' ? 'Ş' : 'D'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography className={outfit.className} sx={{ fontWeight: 600, fontSize: '0.88rem', color: pastel.text }}>
                      {ticket.subject}
                    </Typography>
                  }
                  secondary={
                    <Typography className={outfit.className} sx={{ fontSize: '0.75rem', color: pastel.textMuted, mt: 0.25 }}>
                      {formatRelativeTime(ticket.createdAtUtc)}
                      {ticket.status === 'resolved' && ticket.resolutionMessage ? ` · ${ticket.resolutionMessage.slice(0, 48)}${ticket.resolutionMessage.length > 48 ? '…' : ''}` : ''}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Fade>
      )}

      <ChatPanelModal
        open={newOpen}
        onClose={() => !creating && setNewOpen(false)}
        title="Yeni destek talebi"
        subtitle="Sorununuzu kısaca yazın; OPAS ekibi yanıtlayacaktır."
        actions={
          <>
            <Button onClick={() => setNewOpen(false)} disabled={creating} sx={chatPanelModalGhostBtnSx}>
              Vazgeç
            </Button>
            <Button variant="contained" disabled={!newSubject.trim() || creating} onClick={() => void submitNew()} sx={chatPanelModalPrimaryBtnSx}>
              {creating ? <OpasBrandSpinner size={16} color="#fff" /> : 'Gönder'}
            </Button>
          </>
        }
      >
        <Stack spacing={1.5}>
          <TextField fullWidth label="Konu" value={newSubject} onChange={(e) => setNewSubject(e.target.value.slice(0, 200))} sx={networkFieldSx} />
          <TextField fullWidth multiline minRows={4} maxRows={8} label="Açıklama" value={newBody} onChange={(e) => setNewBody(e.target.value.slice(0, 2000))} sx={networkFieldSx} />
        </Stack>
      </ChatPanelModal>
    </Box>
  )
}

function NetworkFollowRequestsBanner({
  requests,
  onAccept,
  onReject,
}: {
  requests: NetworkFollowRequestIncomingDto[]
  onAccept: (requesterTenantId: string) => void
  onReject: (requesterTenantId: string) => void
}) {
  if (requests.length === 0) return null

  return (
    <Box sx={{ pt: 1, pb: 0.5, fontFamily: outfitFont }}>
      <NetworkProfileText bold sx={{ fontSize: '0.72rem', color: pastel.textMuted, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Takip istekleri
      </NetworkProfileText>
      <Stack spacing={0.75}>
        {requests.map((r) => {
          const name = r.requesterPharmacyDisplayName || r.requesterUsername || r.requesterTenantId.slice(0, 10)
          return (
            <Box
              key={r.requesterTenantId}
              sx={{
                p: 1.1,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.92)',
                border: '1px solid rgba(196,181,253,0.35)',
              }}
            >
              <NetworkProfileText bold sx={{ fontSize: '0.82rem', color: pastel.text }}>
                {name}
              </NetworkProfileText>
              <NetworkProfileText sx={{ fontSize: '0.72rem', color: pastel.textMuted, mt: 0.25 }}>
                Sizi ağına eklemek istiyor
              </NetworkProfileText>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.85 }}>
                <Button
                  className={outfit.className}
                  size="small"
                  variant="contained"
                  onClick={() => void onAccept(r.requesterTenantId)}
                  sx={{ ...networkProfileBtnSx, bgcolor: '#5b21b6', color: '#fff !important', '&:hover': { bgcolor: '#4c1d95' } }}
                >
                  Kabul et
                </Button>
                <Button
                  className={outfit.className}
                  size="small"
                  variant="outlined"
                  onClick={() => void onReject(r.requesterTenantId)}
                  sx={{ ...networkProfileBtnSx, color: `${pastel.text} !important`, borderColor: `${pastel.border} !important`, bgcolor: '#fff' }}
                >
                  Reddet
                </Button>
              </Stack>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

function NetworkWall({
  enabled,
  readOnly,
}: {
  enabled: boolean
  readOnly?: boolean
}) {
  const hataBildir = useHataBildirici()
  const [pollOptionsOpen, setPollOptionsOpen] = useState(false)

  const {
    posts,
    commentsByPost,
    loading,
    loadingMore,
    connected,
    error,
    hasMore,
    loadMore,
    createPost,
    updatePost,
    deletePost,
    toggleLike,
    toggleDislike,
    reportPost,
    toggleCommentLike,
    toggleCommentDislike,
    reportComment,
    loadComments,
    addComment,
    deleteComment,
    votePoll,
    refresh,
  } = usePharmacyNetworkFeed(enabled)

  const { blockUser } = usePharmacyNetworkBlocks()
  const { warnings, markSeen } = usePharmacyNetworkWarnings(enabled && !readOnly)

  const handleBlockAuthor = useCallback(
    async (tenantId: string, userId: string) => {
      await blockUser(tenantId, userId)
      await refresh()
    },
    [blockUser, refresh]
  )

  const {
    follow,
    unfollow,
    cancelOutgoing,
    isFollowingTenant,
    isPendingOutgoingTenant,
    incomingRequests,
    acceptRequest,
    rejectRequest,
  } = usePharmacyNetworkFollows(enabled)
  const [profileView, setProfileView] = useState<NetworkPharmacyProfile | null>(null)
  const [tenantProfileMedia, setTenantProfileMedia] = useState<Record<string, { avatarUrl?: string | null; coverUrl?: string | null }>>({})
  const [followBusy, setFollowBusy] = useState(false)
  const preloadedTenantsRef = useRef(new Set<string>())

  const mergeTenantProfileMedia = useCallback((tenantId: string, avatarUrl: string | null, coverUrl: string | null) => {
    setTenantProfileMedia((prev) => {
      const existing = prev[tenantId]
      if (existing?.avatarUrl === avatarUrl && existing?.coverUrl === coverUrl) return prev
      return { ...prev, [tenantId]: { avatarUrl, coverUrl } }
    })
    setProfileView((prev) => {
      if (!prev || prev.tenantId !== tenantId) return prev
      if (prev.avatarUrl === avatarUrl && prev.coverUrl === coverUrl) return prev
      return { ...prev, avatarUrl, coverUrl }
    })
  }, [])

  useEffect(() => {
    if (!enabled) return
    const tenantIds = new Set<string>()
    for (const post of posts) tenantIds.add(post.tenantId)
    for (const comments of Object.values(commentsByPost)) {
      for (const c of comments) tenantIds.add(c.tenantId)
    }
    if (typeof window !== 'undefined') {
      const ownTenantId = localStorage.getItem('tenantId')
      if (ownTenantId) tenantIds.add(ownTenantId)
    }
    const missing = [...tenantIds].filter((id) => !preloadedTenantsRef.current.has(id))
    prefetchNetworkAvatars([...tenantIds])
    if (missing.length === 0) return

    let cancelled = false
    void (async () => {
      const results = await Promise.all(missing.map((id) => fetchNetworkProfileMedia(id)))
      if (cancelled) return
      for (const r of results) {
        if (r) preloadedTenantsRef.current.add(r.tenantId)
      }
      setTenantProfileMedia((prev) => {
        const next = { ...prev }
        for (const r of results) {
          if (!r) continue
          const existing = next[r.tenantId]
          if (existing?.avatarUrl === r.avatarUrl && existing?.coverUrl === r.coverUrl) continue
          next[r.tenantId] = { avatarUrl: r.avatarUrl, coverUrl: r.coverUrl }
        }
        return next
      })
    })()
    return () => {
      cancelled = true
    }
  }, [enabled, posts, commentsByPost])

  const openProfileView = (next: NetworkPharmacyProfile) => {
    const cached = tenantProfileMedia[next.tenantId]
    setProfileView(
      cached
        ? { ...next, avatarUrl: cached.avatarUrl ?? next.avatarUrl, coverUrl: cached.coverUrl ?? next.coverUrl }
        : next
    )
    void fetchNetworkProfileMedia(next.tenantId).then((r) => {
      if (!r) return
      preloadedTenantsRef.current.add(r.tenantId)
      mergeTenantProfileMedia(r.tenantId, r.avatarUrl ?? null, r.coverUrl ?? null)
      setProfileView((prev) => {
        if (!prev || prev.tenantId !== r.tenantId) return prev
        return { ...prev, profileBio: r.profileBio ?? null }
      })
    })
  }

  const handleProfileMediaUpdated = useCallback((tenantId: string, avatarUrl: string | null, coverUrl: string | null) => {
    preloadedTenantsRef.current.add(tenantId)
    mergeTenantProfileMedia(tenantId, avatarUrl, coverUrl)
  }, [mergeTenantProfileMedia])

  const profile = profileView

  const handleProfileFollow = async () => {
    if (!profile || profile.isOwnPharmacy) return
    setFollowBusy(true)
    try {
      await follow(profile.gln, profile.tenantId)
    } catch (e) {
      hataBildir(e, 'Eklenemedi')
    } finally {
      setFollowBusy(false)
    }
  }

  const handleProfileUnfollow = async () => {
    if (!profile) return
    setFollowBusy(true)
    try {
      await unfollow(profile.tenantId)
    } catch (e) {
      hataBildir(e, 'Takip bırakılamadı')
    } finally {
      setFollowBusy(false)
    }
  }

  const handleProfileCancelOutgoing = async () => {
    if (!profile) return
    setFollowBusy(true)
    try {
      await cancelOutgoing(profile.tenantId)
    } catch (e) {
      hataBildir(e, 'İstek iptal edilemedi')
    } finally {
      setFollowBusy(false)
    }
  }

  const [draft, setDraft] = useState('')
  const [composeImages, setComposeImages] = useState<string[]>([])
  const composeImagesFileRef = useRef<HTMLInputElement | null>(null)
  const [composeMode, setComposeMode] = useState<'text' | 'poll'>('text')
  const [pollOptions, setPollOptions] = useState(['', ''])
  const [isPharmacist, setIsPharmacist] = useState(false)
  const [sending, setSending] = useState(false)
  const [publishError, setPublishError] = useState<string | null>(null)
  const feedRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!enabled) return
    setIsPharmacist(typeof window !== 'undefined' && localStorage.getItem('userType') === 'tenant')
  }, [enabled])

  const canPublishPoll = composeMode === 'poll' && isPharmacist
  const pollOptionsValid = pollOptions.map((o) => o.trim()).filter(Boolean).length >= 2
  const filledPollCount = pollOptions.map((o) => o.trim()).filter(Boolean).length

  const handlePublish = async () => {
    const text = draft.trim()
    const images = composeImages
    if ((!text && images.length === 0) || sending) return
    if (canPublishPoll && !pollOptionsValid) {
      setPublishError('Anket için en az 2 seçenek girin.')
      return
    }
    setSending(true)
    setPublishError(null)
    const savedDraft = text
    const savedPollOptions = [...pollOptions]
    const savedImages = [...images]
    setDraft('')
    setComposeImages([])
    try {
      if (canPublishPoll) {
        await createPost(text, savedPollOptions.map((o) => o.trim()).filter(Boolean))
        setPollOptions(['', ''])
        setComposeMode('text')
        setPollOptionsOpen(false)
      } else {
        await createPost(text, undefined, savedImages.length > 0 ? savedImages : undefined)
      }
      feedRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (e) {
      setDraft(savedDraft)
      setComposeImages(savedImages)
      if (canPublishPoll) setPollOptions(savedPollOptions)
      setPublishError(e instanceof Error ? e.message : 'Paylaşım gönderilemedi')
    } finally {
      setSending(false)
    }
  }

  if (profile) {
    const profileRecentPosts = posts.filter((p) => p.tenantId === profile.tenantId).slice(0, 4)
    return (
      <NetworkPharmacyProfilePanel
        key={profile.tenantId}
        profile={profile}
        onMediaUpdated={handleProfileMediaUpdated}
        onBack={() => setProfileView(null)}
        isFollowing={isFollowingTenant(profile.tenantId)}
        isPendingOutgoing={isPendingOutgoingTenant(profile.tenantId)}
        followBusy={followBusy}
        onFollow={handleProfileFollow}
        onUnfollow={handleProfileUnfollow}
        onCancelOutgoing={handleProfileCancelOutgoing}
        recentPosts={profileRecentPosts}
      />
    )
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {!readOnly && (
        <Box sx={{ px: 2 }}>
          <NetworkFollowRequestsBanner
            requests={incomingRequests}
            onAccept={async (id) => {
              try {
                await acceptRequest(id)
              } catch (e) {
                hataBildir(e, 'Kabul edilemedi')
              }
            }}
            onReject={async (id) => {
              try {
                await rejectRequest(id)
              } catch (e) {
                hataBildir(e, 'Reddedilemedi')
              }
            }}
          />
        </Box>
      )}
      {readOnly && (
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Typography className={outfit.className} sx={{ fontSize: '0.72rem', color: pastel.textMuted, lineHeight: 1.45 }}>
            SuperAdmin moderasyon — paylaşım ve yorumları düzenleyebilir, kaldırabilir; şikayetler Destek sekmesinde listelenir.
          </Typography>
        </Box>
      )}
      {!readOnly && warnings.length > 0 && (
        <Paper elevation={0} sx={{ mx: 2, mt: 1, mb: 0.5, p: 1.1, borderRadius: 2, bgcolor: 'rgba(254,226,226,0.55)', border: '1px solid rgba(248,113,113,0.35)' }}>
          {warnings.map((w) => (
            <Box key={w.id} sx={{ '& + &': { mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(248,113,113,0.2)' } }}>
              <Typography className={outfit.className} sx={{ fontSize: '0.78rem', color: '#991b1b', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                {w.message}
              </Typography>
              <Button
                size="small"
                onClick={() => void markSeen(w.id)}
                sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.72rem', color: '#b91c1c' }}
              >
                Anladım
              </Button>
            </Box>
          ))}
        </Paper>
      )}
      {!readOnly && (
      <Paper elevation={0} sx={networkComposeBoxSx}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          variant="standard"
          placeholder={canPublishPoll ? 'Anket sorunuzu yazın…' : 'Meslektaşlarınızla bir şeyler paylaşın…'}
          value={draft}
          onChange={(e) => {
            setPublishError(null)
            setDraft(e.target.value.slice(0, 4000))
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && composeMode !== 'poll') {
              e.preventDefault()
              void handlePublish()
            }
          }}
          InputProps={{ disableUnderline: true }}
        />
        {!canPublishPoll && (
          <>
            <input
              ref={composeImagesFileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              hidden
              onChange={(e) => {
                void pickNetworkPostImages(e.target.files, composeImages, setComposeImages, hataBildir)
                e.target.value = ''
              }}
            />
            <NetworkComposeImagePreviews
              images={composeImages}
              onRemove={(idx) => setComposeImages((prev) => prev.filter((_, i) => i !== idx))}
            />
          </>
        )}
        {canPublishPoll && (
          <>
            <Button
              size="small"
              onClick={() => setPollOptionsOpen((v) => !v)}
              endIcon={
                <ExpandMoreRoundedIcon
                  sx={{
                    fontSize: 18,
                    transform: pollOptionsOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s ease',
                  }}
                />
              }
              sx={{
                mt: 0.75,
                textTransform: 'none',
                fontFamily: outfit.style.fontFamily,
                fontSize: '0.72rem',
                fontWeight: 600,
                color: '#6d28d9',
                px: 0.5,
                minHeight: 28,
                '&:hover': { bgcolor: 'rgba(237,233,254,0.45)' },
              }}
            >
              Seçenekler{filledPollCount > 0 ? ` (${filledPollCount})` : ''}
            </Button>
            <Collapse in={pollOptionsOpen}>
              <Stack spacing={0.65} sx={{ mt: 0.65, pt: 0.5, borderTop: '1px solid rgba(196,181,253,0.22)' }}>
                {pollOptions.map((opt, idx) => (
                  <TextField
                    key={idx}
                    fullWidth
                    size="small"
                    placeholder={`Seçenek ${idx + 1}`}
                    value={opt}
                    onChange={(e) => {
                      setPublishError(null)
                      setPollOptions((prev) => prev.map((v, i) => (i === idx ? e.target.value.slice(0, 200) : v)))
                    }}
                    sx={networkFieldSx}
                  />
                ))}
                {pollOptions.length < 6 && (
                  <Button
                    size="small"
                    startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setPollOptions((prev) => [...prev, ''])}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none', fontFamily: outfit.style.fontFamily, fontSize: '0.72rem', color: pastel.textMuted }}
                  >
                    Seçenek ekle
                  </Button>
                )}
              </Stack>
            </Collapse>
          </>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 0.75, borderTop: '1px solid rgba(196,181,253,0.18)' }}>
          <Stack direction="row" spacing={0.35} alignItems="center">
            {isPharmacist && (
              <>
                <IconButton
                  size="small"
                  aria-label="Metin paylaşımı"
                  onClick={() => {
                    setComposeMode('text')
                    setPollOptionsOpen(false)
                  }}
                  sx={{
                    bgcolor: composeMode === 'text' ? 'rgba(237,233,254,0.95)' : 'transparent',
                    border: composeMode === 'text' ? `1px solid ${pastel.border}` : '1px solid transparent',
                    color: composeMode === 'text' ? '#6d28d9' : pastel.textMuted,
                  }}
                >
                  <CreateOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
                <Tooltip title="Anket oluştur" {...networkIconTooltipProps}>
                  <IconButton
                    size="small"
                    aria-label="Anket oluştur"
                    onClick={() => {
                      setComposeMode('poll')
                      setPollOptionsOpen(false)
                      setComposeImages([])
                    }}
                    sx={{
                      bgcolor: composeMode === 'poll' ? 'rgba(237,233,254,0.95)' : 'transparent',
                      border: composeMode === 'poll' ? `1px solid ${pastel.border}` : '1px solid transparent',
                      color: composeMode === 'poll' ? '#6d28d9' : pastel.textMuted,
                    }}
                  >
                    <PollOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {!canPublishPoll && (
              <Tooltip title="Görsel ekle (en fazla 2)" {...networkIconTooltipProps}>
                <IconButton
                  size="small"
                  aria-label="Görsel ekle"
                  disabled={composeImages.length >= MAX_NETWORK_POST_IMAGES}
                  onClick={() => composeImagesFileRef.current?.click()}
                  sx={{ color: composeImages.length > 0 ? '#6d28d9' : pastel.textMuted }}
                >
                  <ImageOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
            <Typography className={outfit.className} sx={{ fontSize: '0.62rem', fontWeight: 500, color: pastel.textMuted, ml: 0.25 }}>
              {connected ? '● Canlı' : loading ? '…' : 'Bağlantı yok'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {publishError && (
              <Typography className={outfit.className} sx={{ fontSize: '0.68rem', color: '#b91c1c', maxWidth: 140 }} noWrap>
                {publishError}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={() => void handlePublish()}
              disabled={(!draft.trim() && composeImages.length === 0) || sending || (canPublishPoll && !pollOptionsValid)}
              aria-label="Paylaş"
              sx={{
                bgcolor: '#5b21b6',
                color: '#fff',
                width: 36,
                height: 36,
                boxShadow: '0 4px 14px rgba(91,33,182,0.28)',
                '&:hover': { bgcolor: '#4c1d95' },
                '&.Mui-disabled': { bgcolor: 'rgba(196,181,253,0.45)', color: 'rgba(255,255,255,0.85)' },
              }}
            >
              {sending ? <OpasBrandSpinner size={18} color="#fff" /> : <SendRoundedIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Stack>
        </Box>
      </Paper>
      )}

      <Box ref={feedRef} sx={{ flex: 1, overflow: 'auto', minHeight: 80, px: 2, pb: 2, pt: 0.5, fontFamily: outfit.style.fontFamily }}>
        {loading && posts.length === 0 ? (
          <OpasBrandLoading label="Akış yükleniyor…" sx={{ minHeight: 120 }} />
        ) : (
          <>
            {posts.length === 0 && !loading && error && (
              <Typography className={outfit.className} sx={{ color: pastel.textMuted, fontSize: '0.85rem' }}>
                Akış şu an yüklenemedi. Lütfen tekrar deneyin.
              </Typography>
            )}
            {posts.length === 0 && !loading && !error && (
              <Typography className={outfit.className} sx={{ color: pastel.textMuted, fontSize: '0.85rem' }}>
                Henüz paylaşım yok. İlk duyuruyu siz yapın.
              </Typography>
            )}
            {posts.map((post) => (
              <NetworkPostCard
                key={post.id}
                post={post}
                comments={commentsByPost[post.id]}
                feedPosts={posts}
                onUpdate={updatePost}
                onDelete={deletePost}
                onOpenProfile={openProfileView}
                onToggleLike={toggleLike}
                onToggleDislike={toggleDislike}
                onToggleCommentLike={toggleCommentLike}
                onToggleCommentDislike={toggleCommentDislike}
                onReportPost={reportPost}
                onReportComment={reportComment}
                onLoadComments={loadComments}
                onAddComment={addComment}
                onDeleteComment={deleteComment}
                onVotePoll={votePoll}
                onBlockAuthor={handleBlockAuthor}
                allowUserBlock={!readOnly}
              />
            ))}
            {hasMore && (
              <Button
                fullWidth
                size="small"
                onClick={loadMore}
                disabled={loadingMore}
                sx={{ mt: 0.5, textTransform: 'none', color: pastel.textMuted, fontFamily: outfit.style.fontFamily, minHeight: 36 }}
              >
                {loadingMore ? <OpasBrandSpinner size={20} /> : 'Daha fazla'}
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

function DmMessageBubble({ m }: { m: NetworkDmMessageDto }) {
  const theme = bubbleThemeForSender(m.senderTenantId)
  const name = m.senderUsername?.trim() || m.senderTenantId.slice(0, 10)
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: m.isOwn ? 'flex-end' : 'flex-start', mb: 1.35, maxWidth: '94%', alignSelf: m.isOwn ? 'flex-end' : 'flex-start', ml: m.isOwn ? 'auto' : 0 }}>
      <Box sx={{ px: 0.5, mb: 0.5, textAlign: m.isOwn ? 'right' : 'left' }}>
        <Typography className={outfit.className} sx={{ fontWeight: 800, fontSize: '0.78rem', color: theme.name }}>{name}</Typography>
        <Typography className={outfit.className} sx={{ fontSize: '0.68rem', fontWeight: 500, color: pastel.textMuted }}>{formatTime(m.createdAtUtc)}</Typography>
      </Box>
      <Box sx={{ px: 1.35, py: 1.1, borderRadius: 2.25, bgcolor: theme.bg, border: `1.5px solid ${theme.border}`, color: theme.body, fontSize: '0.84rem', fontFamily: outfit.style.fontFamily, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {m.body}
      </Box>
    </Box>
  )
}

function NetworkDmConversation({
  thread,
  enabled,
  onBack,
}: {
  thread: NetworkDmThreadDto
  enabled: boolean
  onBack: () => void
}) {
  const hataBildir = useHataBildirici()
  const { messages, loading, connected, send } = usePharmacyNetworkDm(thread.id, enabled)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const bottomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  const handleSend = async () => {
    const t = draft.trim()
    if (!t || sending) return
    setSending(true)
    setDraft('')
    try {
      await send(t)
    } catch (e) {
      hataBildir(e, 'Gönderilemedi')
      setDraft(t)
    } finally {
      setSending(false)
    }
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, px: 2, pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, pt: 0.5 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: pastel.textMuted }}>
          <Typography component="span" sx={{ fontSize: '1.2rem' }}>←</Typography>
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography className={outfit.className} sx={{ fontWeight: 700, fontSize: '0.9rem', color: pastel.text }}>{thread.peerDisplayName}</Typography>
          <Typography className={outfit.className} sx={{ fontSize: '0.62rem', color: pastel.textMuted }}>
            {[thread.peerUsername, thread.peerTown, thread.peerCity].filter(Boolean).join(' · ') || thread.peerGln || ''}
          </Typography>
          {thread.isOversight && thread.localUsername ? (
            <Typography className={outfit.className} sx={{ fontSize: '0.6rem', color: pastel.textMuted, fontStyle: 'italic' }}>
              Alt kullanıcı: {thread.localUsername}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', borderRadius: 2, p: 1.25, bgcolor: 'rgba(255,255,255,0.75)', border: '1px solid rgba(196,181,253,0.2)' }}>
        {loading && messages.length === 0 ? (
          <OpasBrandLoading label="Mesajlar yükleniyor…" sx={{ minHeight: 100, py: 2 }} />
        ) : (
          messages.map((m) => <DmMessageBubble key={m.id} m={m} />)
        )}
        <div ref={bottomRef} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="Mesaj yazın…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void handleSend()
            }
          }}
          sx={networkFieldSx}
        />
        <IconButton size="small" onClick={() => void handleSend()} disabled={!draft.trim() || sending} aria-label="Gönder">
          {sending ? <OpasBrandSpinner size={20} /> : <SendRoundedIcon sx={{ fontSize: 20, color: pastel.textMuted }} />}
        </IconButton>
      </Box>
      <Typography className={outfit.className} sx={{ fontSize: '0.62rem', fontWeight: 500, color: pastel.textMuted, mt: 0.5 }}>
        {connected ? '● Canlı' : '…'}
      </Typography>
    </Box>
  )
}

function NetworkMessages({ enabled }: { enabled: boolean }) {
  const hataBildir = useHataBildirici()
  const isPharmacist = typeof window !== 'undefined' && localStorage.getItem('userType') === 'tenant'
  const { dmEnabled, loading: settingsLoading, updateDmEnabled } = usePharmacyNetworkSettings(enabled)
  const { threads, loading, refresh, openThread } = usePharmacyNetworkThreads(enabled)
  const { follows, loading: followsLoading, follow, unfollow, isFollowingGln, isPendingOutgoingGln, incomingRequests, acceptRequest, rejectRequest } = usePharmacyNetworkFollows(enabled)
  const [selected, setSelected] = useState<NetworkDmThreadDto | null>(null)
  const [search, setSearch] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<NetworkDmSearchHit[]>([])
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!enabled) setSelected(null)
  }, [enabled])

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    const q = search.trim()
    if (q.length < 2) {
      setSearchResults([])
      setSearching(false)
      return
    }
    setSearching(true)
    searchTimerRef.current = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(
            `/api/opas/pharmacy-network/dm/search?q=${encodeURIComponent(q)}&take=12`,
            { credentials: 'include', cache: 'no-store' }
          )
          const data = await res.json()
          setSearchResults((data.items ?? []) as NetworkDmSearchHit[])
        } catch {
          setSearchResults([])
        } finally {
          setSearching(false)
        }
      })()
    }, 280)
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    }
  }, [search])

  const startChatWithHit = async (hit: NetworkDmSearchHit) => {
    try {
      const item = await openThread(hit.peerTenantId, hit.peerGln ?? undefined, hit.peerUserId)
      setSelected(item)
      setSearch('')
      setSearchResults([])
    } catch (e) {
      hataBildir(e, 'Sohbet açılamadı')
    }
  }

  const startChatWithFollow = async (f: { tenantId: string; gln?: string | null; pharmacyDisplayName?: string | null }) => {
    try {
      const item = await openThread(f.tenantId, f.gln ?? undefined, f.tenantId)
      setSelected(item)
    } catch (e) {
      hataBildir(e, 'Sohbet açılamadı')
    }
  }

  const handleFollowGln = async (gln: string) => {
    try {
      await follow(gln)
    } catch (e) {
      hataBildir(e, 'İstek gönderilemedi')
    }
  }

  if (selected) {
    return <NetworkDmConversation thread={selected} enabled={enabled} onBack={() => { setSelected(null); void refresh() }} />
  }

  const listInitialLoading =
    enabled &&
    settingsLoading &&
    followsLoading &&
    loading &&
    follows.length === 0 &&
    threads.length === 0

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, px: 2, pb: 2 }}>
      {listInitialLoading ? (
        <OpasBrandLoading label="Yükleniyor…" sx={{ flex: 1, minHeight: 160 }} />
      ) : (
        <>
      <NetworkFollowRequestsBanner
        requests={incomingRequests}
        onAccept={async (id) => {
          try { await acceptRequest(id) } catch (e) { hataBildir(e, 'Kabul edilemedi') }
        }}
        onReject={async (id) => {
          try { await rejectRequest(id) } catch (e) { hataBildir(e, 'Reddedilemedi') }
        }}
      />
      {isPharmacist ? (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={dmEnabled}
              onChange={(e) => {
                void updateDmEnabled(e.target.checked).catch((err) => hataBildir(err, 'Kaydedilemedi'))
              }}
            />
          }
          label={
            <Typography className={outfit.className} sx={{ fontSize: '0.78rem', color: pastel.text }}>
              Eczaneler arası özel mesaj kabul et
            </Typography>
          }
          sx={{ mx: 0, mb: 1 }}
        />
      ) : (
        <Typography className={outfit.className} sx={{ fontSize: '0.72rem', color: pastel.textMuted, mb: 1 }}>
          {dmEnabled ? 'Eczaneniz özel mesaj kabul ediyor.' : 'Eczaneniz şu an özel mesaj kabul etmiyor.'}
        </Typography>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="GLN, eczane, kullanıcı, il, ilçe, eczacı adı…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          endAdornment: searching ? (
            <InputAdornment position="end"><OpasBrandSpinner size={16} /></InputAdornment>
          ) : undefined,
        }}
        sx={{ ...networkFieldSx, mb: 1 }}
      />

      {searchResults.length > 0 && (
        <List dense sx={{ mb: 1, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 2, maxHeight: 180, overflow: 'auto' }}>
          {searchResults.map((r) => (
            <ListItemButton key={`${r.peerTenantId}:${r.peerUserId}`} onClick={() => void startChatWithHit(r)} sx={{ pr: 1 }}>
              <ListItemText
                primary={r.kind === 'user'
                  ? (r.peerPersonName || r.peerUsername || r.peerDisplayName)
                  : r.peerDisplayName}
                secondary={[
                  r.kind === 'user' ? r.peerDisplayName : null,
                  r.peerUsername,
                  r.kind === 'pharmacy' ? r.peerPersonName : null,
                  r.peerTown,
                  r.peerCity,
                  r.peerGln,
                ].filter(Boolean).join(' · ')}
                primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 600 }}
                secondaryTypographyProps={{ fontSize: '0.68rem' }}
              />
              {r.peerGln ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isFollowingGln(r.peerGln) || isPendingOutgoingGln(r.peerGln)}
                  onClick={(e) => { e.stopPropagation(); void handleFollowGln(r.peerGln!) }}
                  sx={{ textTransform: 'none', fontSize: '0.68rem', flexShrink: 0, minWidth: 56, fontFamily: outfitFont }}
                >
                  {isFollowingGln(r.peerGln) ? 'Takipte' : isPendingOutgoingGln(r.peerGln) ? 'Bekliyor' : 'Takip'}
                </Button>
              ) : null}
            </ListItemButton>
          ))}
        </List>
      )}

      <Typography className={outfit.className} sx={{ fontSize: '0.72rem', fontWeight: 700, color: pastel.textMuted, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Takip edilen eczaneler
      </Typography>

      <List dense sx={{ mb: 1, maxHeight: 120, overflow: 'auto' }}>
        {followsLoading && follows.length === 0 && (
          <OpasBrandLoading size={32} sx={{ py: 1.5 }} />
        )}
        {!followsLoading && follows.length === 0 && (
          <Typography sx={{ fontSize: '0.78rem', color: pastel.textMuted, px: 1 }}>Henüz takip ettiğiniz eczane yok.</Typography>
        )}
        {follows.map((f) => (
          <ListItemButton
            key={f.tenantId}
            onClick={() => void startChatWithFollow(f)}
            sx={{ borderRadius: 1.5, mb: 0.35, py: 0.35, bgcolor: 'rgba(255,255,255,0.45)' }}
          >
            <ListItemText
              primary={f.pharmacyDisplayName || f.gln || f.tenantId.slice(0, 8)}
              secondary={[f.town, f.city].filter(Boolean).join(', ')}
              primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.65rem' }}
            />
            <Button
              size="small"
              color="inherit"
              onClick={(e) => {
                e.stopPropagation()
                void unfollow(f.tenantId).catch((err) => hataBildir(err, 'Takip bırakılamadı'))
              }}
              sx={{ textTransform: 'none', fontSize: '0.65rem', flexShrink: 0, color: pastel.textMuted }}
            >
              Bırak
            </Button>
          </ListItemButton>
        ))}
      </List>

      <Typography className={outfit.className} sx={{ fontSize: '0.72rem', fontWeight: 700, color: pastel.textMuted, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Sohbetler
      </Typography>

      <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
        {loading && threads.length === 0 && !followsLoading && (
          <OpasBrandLoading label="Sohbetler yükleniyor…" size={36} sx={{ py: 2 }} />
        )}
        {!loading && threads.length === 0 && (
          <Typography sx={{ fontSize: '0.82rem', color: pastel.textMuted, px: 1 }}>Henüz özel mesaj yok.</Typography>
        )}
        {threads.map((t) => (
          <ListItemButton
            key={t.id}
            onClick={() => setSelected(t)}
            sx={{ borderRadius: 2, mb: 0.75, bgcolor: 'rgba(255,255,255,0.55)', border: '1px solid rgba(196,181,253,0.25)' }}
          >
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: pastel.lavender, color: pastel.text, fontWeight: 700, fontSize: '0.8rem' }}>
                {nameInitials(t.peerDisplayName)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={t.isOversight && t.localUsername
                ? `${t.peerDisplayName} · ${t.localUsername}`
                : t.peerDisplayName}
              secondary={t.lastMessagePreview || 'Sohbet başlatıldı'}
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.72rem', noWrap: true }}
            />
          </ListItemButton>
        ))}
      </List>
        </>
      )}
    </Box>
  )
}

function NetworkChannel({
  panelOpen,
  tabActive,
  subTab,
  onSubTabChange,
  readOnly,
}: {
  panelOpen: boolean
  tabActive: boolean
  subTab: NetworkSubTab
  onSubTabChange: (tab: NetworkSubTab) => void
  readOnly?: boolean
}) {
  const enabled = panelOpen && tabActive

  if (readOnly) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <NetworkWall enabled={enabled} readOnly />
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Tabs
        value={subTab}
        onChange={(_, v) => onSubTabChange(v)}
        variant="fullWidth"
        sx={{
          minHeight: 36,
          borderBottom: `1px solid ${pastel.border}`,
          px: 1,
          '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none', fontFamily: outfit.style.fontFamily, fontSize: '0.78rem', fontWeight: 600 },
        }}
      >
        <Tab value="wall" label="Duvar" />
        <Tab value="messages" label="Özel mesaj" />
      </Tabs>
      {subTab === 'wall' && <NetworkWall enabled={enabled} />}
      {subTab === 'messages' && <NetworkMessages enabled={enabled} />}
    </Box>
  )
}

function ChatNotificationSettingsMenu() {
  const theme = useTheme()
  const overlayZ = chatPanelOverlayZ(theme)
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [prefs, setPrefs] = useState(getDefaultChatNotificationPrefs)

  useEffect(() => {
    void loadChatNotificationPrefs().then(setPrefs)
  }, [])

  const apply = (patch: Partial<ReturnType<typeof readChatNotificationPrefs>>) => {
    const next = { ...prefs, ...patch }
    setPrefs(next)
    writeChatNotificationPrefs(next)
    if (patch.desktop && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      void Notification.requestPermission()
    }
  }

  return (
    <>
      <IconButton
        size="small"
        aria-label="Bildirim ayarları"
        onClick={(e) => {
          e.stopPropagation()
          setAnchor(e.currentTarget)
        }}
        sx={{
          color: pastel.textMuted,
          bgcolor: 'rgba(255,255,255,0.7)',
          border: `1px solid ${pastel.border}`,
          '&:hover': { bgcolor: '#fff', color: pastel.text },
        }}
      >
        <SettingsOutlinedIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        sx={{ zIndex: overlayZ + 1 }}
        slotProps={{
          paper: { sx: { ...networkActionMenuPaperSx, zIndex: overlayZ + 1, minWidth: 228, py: 0.75 } },
          list: { onClick: (e: React.MouseEvent) => e.stopPropagation() },
        }}
      >
        <Box sx={{ px: 1.5, py: 0.5 }}>
          <Typography className={outfit.className} sx={{ fontSize: '0.72rem', fontWeight: 700, color: pastel.textMuted, letterSpacing: '0.04em' }}>
            BİLDİRİMLER
          </Typography>
        </Box>
        <MenuItem disableRipple sx={{ py: 0.25, px: 1.25, '&:hover': { bgcolor: 'transparent' } }}>
          <FormControlLabel
            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
            labelPlacement="start"
            control={
              <Switch
                size="small"
                checked={prefs.sound}
                onChange={(_, v) => apply({ sound: v })}
              />
            }
            label={<Typography className={outfit.className} sx={{ fontSize: '0.82rem', color: pastel.text }}>Sesli bildirim</Typography>}
          />
        </MenuItem>
        <MenuItem disableRipple sx={{ py: 0.25, px: 1.25, '&:hover': { bgcolor: 'transparent' } }}>
          <FormControlLabel
            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
            labelPlacement="start"
            control={
              <Switch
                size="small"
                checked={prefs.desktop}
                onChange={(_, v) => apply({ desktop: v })}
              />
            }
            label={<Typography className={outfit.className} sx={{ fontSize: '0.82rem', color: pastel.text }}>Masaüstü bildirimi</Typography>}
          />
        </MenuItem>
      </Menu>
    </>
  )
}

export default function TenantChatPanel({ open, onClose, openTarget, onOpenTargetConsumed }: TenantChatPanelProps) {
  const theme = useTheme()
  const pathname = usePathname()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isSuperAdmin = isChatPanelSuperAdmin(pathname)
  const [tab, setTab] = useState<ChannelTab>(() => {
    if (typeof window === 'undefined') return 'internal'
    return isChatPanelSuperAdmin(window.location.pathname) ? 'network' : 'internal'
  })
  const [networkSubTab, setNetworkSubTab] = useState<NetworkSubTab>('wall')

  const { pendingCount, refreshPendingCount } = usePharmacyNetworkModeration(isSuperAdmin && open)
  const { unreadCount: supportUnreadCount, refreshUnreadCount: refreshSupportUnread } = usePharmacyNetworkSupport(!isSuperAdmin && open)

  useEffect(() => {
    if (isSuperAdmin && open) void refreshPendingCount()
    if (!isSuperAdmin && open) void refreshSupportUnread()
  }, [isSuperAdmin, open, tab, refreshPendingCount, refreshSupportUnread])

  useEffect(() => {
    if (open && isSuperAdmin) {
      setTab((t) => (t === 'internal' ? 'network' : t))
      setNetworkSubTab('wall')
    }
  }, [open, isSuperAdmin])

  useEffect(() => {
    if (!open || !openTarget) return
    if (isSuperAdmin) {
      if (openTarget.tab === 'support' || openTarget.networkSubTab === 'moderation') {
        setTab('support')
      } else {
        setTab('network')
        setNetworkSubTab(openTarget.networkSubTab === 'messages' ? 'wall' : (openTarget.networkSubTab ?? 'wall'))
      }
    } else {
      setTab(openTarget.tab)
      if (openTarget.tab === 'network' && openTarget.networkSubTab) {
        setNetworkSubTab(openTarget.networkSubTab === 'moderation' ? 'wall' : openTarget.networkSubTab)
      }
    }
    onOpenTargetConsumed?.()
  }, [open, openTarget, onOpenTargetConsumed, isSuperAdmin])

  const drawerWidth = isMobile ? '100%' : 480

  const tabSubtitle = isSuperAdmin
    ? tab === 'support'
      ? 'Eczaneler arası şikayet ve bildirimler'
      : 'Eczaneler arası duvar — izleme'
    : tab === 'internal'
      ? 'Eczane içi canlı mesajlaşma'
      : tab === 'network'
        ? 'Duvar ve eczaneler arası özel mesaj'
        : 'Destek talepleri ve şikayetleriniz'

  const supportTabLabel =
    isSuperAdmin && pendingCount > 0 ? (
      <Badge
        color="error"
        badgeContent={pendingCount > 99 ? '99+' : pendingCount}
        sx={{ '& .MuiBadge-badge': { fontSize: '0.62rem', minWidth: 16, height: 16, top: 2, right: -6 } }}
      >
        Destek
      </Badge>
    ) : !isSuperAdmin && supportUnreadCount > 0 ? (
      <Badge
        color="error"
        badgeContent={supportUnreadCount > 99 ? '99+' : supportUnreadCount}
        sx={{ '& .MuiBadge-badge': { fontSize: '0.62rem', minWidth: 16, height: 16, top: 2, right: -6 } }}
      >
        Destek
      </Badge>
    ) : (
      'Destek'
    )

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={{ enter: 380, exit: 280 }}
      sx={(t) => ({ zIndex: t.zIndex.modal + CHAT_DRAWER_Z_OFFSET })}
      ModalProps={{ disableEnforceFocus: true }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: 'rgba(35, 28, 48, 0.35)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          width: drawerWidth,
          maxWidth: '100vw',
          background: pastel.bg,
          borderLeft: `1px solid ${pastel.border}`,
          boxShadow: pastel.shadow,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${pastel.border}`,
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                background: `linear-gradient(135deg, ${pastel.lavender} 0%, ${pastel.mint} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(139, 122, 184, 0.25)',
              }}
            >
              <ChatBubbleOutlineRoundedIcon sx={{ color: pastel.text, fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                className={outfit.className}
                sx={{
                  fontWeight: 700,
                  fontSize: '1.125rem',
                  color: pastel.text,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}
              >
                Mesajlar
              </Typography>
              <Typography className={outfit.className} sx={{ fontSize: '0.7rem', color: pastel.textMuted, mt: 0.25 }}>
                {tabSubtitle}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ChatNotificationSettingsMenu />
            <IconButton
            onClick={onClose}
            size="small"
            aria-label="Kapat"
            sx={{
              color: pastel.textMuted,
              bgcolor: 'rgba(255,255,255,0.7)',
              border: `1px solid ${pastel.border}`,
              '&:hover': { bgcolor: '#fff', color: pastel.text },
              transition: 'all 0.2s ease',
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
          </Box>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => {
            if (isSuperAdmin && v === 'internal') return
            setTab(v)
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mt: 2,
            minHeight: 40,
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: `linear-gradient(90deg, #a78bfa, #34d399)`,
            },
            '& .MuiTab-root': {
              minHeight: 40,
              py: 0.75,
              textTransform: 'none',
              fontFamily: outfit.style.fontFamily,
              fontSize: '0.75rem',
              fontWeight: 600,
              color: pastel.textMuted,
              '&.Mui-selected': { color: pastel.text },
            },
          }}
        >
          {!isSuperAdmin && (
            <Tab value="internal" icon={<StorefrontRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Eczane içi" />
          )}
          <Tab value="network" icon={<PublicRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Eczaneler arası" />
          <Tab value="support" icon={<SupportAgentRoundedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label={supportTabLabel} />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {tab === 'internal' && !isSuperAdmin && <InternalChannel panelOpen={open} tabActive={tab === 'internal'} />}
        {tab === 'network' && (
          <NetworkChannel
            panelOpen={open}
            tabActive={tab === 'network'}
            subTab={networkSubTab}
            onSubTabChange={setNetworkSubTab}
            readOnly={isSuperAdmin}
          />
        )}

        {tab === 'support' && isSuperAdmin && (
          <NetworkModerationPanel enabled={open} onQueueChanged={() => void refreshPendingCount()} />
        )}

        {tab === 'support' && !isSuperAdmin && (
          <TenantSupportChannel enabled={open && tab === 'support'} onQueueChanged={() => void refreshSupportUnread()} />
        )}
      </Box>
      </Box>
    </Drawer>
  )
}
